// ═══════════════════════════════════════════════════════════════
// Sistema i18n minimalista — sin dependencias.
// Soporta ES / CA / EN / FR (como easyrural.net).
// Uso: const t = useT();  →  <h2>{t("nav.dashboard")}</h2>
// ═══════════════════════════════════════════════════════════════
import { createContext, useContext, useState, useEffect } from "react";

const ES = {
  // ─── PANEL ADMIN ───────────────────────────────────────
  "common.loading": "Cargando…",
  "common.error": "Error",
  "common.retry": "Reintentar",
  "common.noData": "Sin datos",
  "common.connected": "Conectado",
  "common.waitingSensors": "Esperando datos de sensores…",
  "common.waiting": "Esperando datos…",
  "common.live": "EN VIVO",
  "common.backHome": "← Inicio",

  "nav.status": "Estado sensores",
  "nav.map": "Mapa alojamientos",
  "nav.dashboard": "Dashboard",
  "nav.consumption": "Consumo y costes",
  "nav.forecast": "Predicción",
  "nav.contract": "Contrato eléctrico",
  "nav.savings": "Ahorro",
  "nav.alarms": "Notificaciones",
  "nav.actuators": "Mantenimiento",
  "nav.install": "Instalación",
  "nav.sec.Gestión": "Gestión",
  "nav.sec.Alojamiento": "Alojamiento",
  "nav.sec.Equipos": "Equipos",
  "nav.newClient": "Alta cliente",
  "nav.agenda": "Agenda",
  "nav.guests": "Huéspedes",
  "nav.access": "Accesos",
  "nav.casaRural": "Casa Rural",
  "nav.version": "Versión 9.0 (live)",

  "login.title": "Panel de administración",
  "login.passwordLabel": "Contraseña",
  "login.submit": "Entrar",
  "login.error": "Contraseña incorrecta",

  "header.logout": "Salir",
  "header.adminPanel": "Panel admin",

  "footer.copyright": "© 2026 EasyRural · Módulo Energía · UPC + EasyRural (DIH 2024)",
  "footer.support": "Soporte",

  "status.title": "Estado del alojamiento",
  "status.subtitle": "Servicios activos en {name}",
  "status.plan": "Plan",
  "status.activeServices": "Servicios activos",
  "status.installedDevices": "Dispositivos instalados",
  "status.devicesUnit": "dispositivos",
  "status.alarms7d": "Alarmas (7D)",
  "status.alarmsUnit": "eventos",
  "status.availability30d": "Disponibilidad 30 días",
  "status.notInstalled": "No instalado",
  "status.noRecent": "Sin lecturas recientes",
  "status.deviceSingle": "dispositivo",
  "status.devicePlural": "dispositivos",
  "status.actuator": "actuador",
  "status.actuatorPlural": "actuadores",
  "status.sensors.temp": "Sensores de temperatura",
  "status.sensors.electric": "Sensores eléctricos",
  "status.sensors.water": "Sensores de agua",
  "status.sensors.consumption": "Consumo eléctrico",
  "status.sensors.meteo": "Meteorología",
  "status.sensors.price": "Precio de la luz",
  "status.sensors.co2": "Huella de carbono",
  "status.sensors.alarms": "Alarmas y avisos",
  "status.sensors.control": "Control remoto",
  "status.sensors.connection": "Conexión sensores",
  "status.calc": "Cálculo automático",
  "status.botTelegram": "Bot Telegram",
  "status.valve": "Válvula automática",
  "status.localServer": "Servidor local",

  "map.title": "Mapa de alojamientos",
  "map.subtitle": "Distribución geográfica de los alojamientos gestionados",

  "dashboard.title": "Dashboard",
  "dashboard.subtitle": "Resumen en tiempo real del alojamiento",

  "pvpc.cheapest": "MÁS BARATA",
  "pvpc.now": "AHORA",
  "pvpc.expensive": "MÁS CARA",
  "pvpc.cleanest": "MÁS LIMPIA",
  "pvpc.dirtiest": "MÁS SUCIA",

  "consumption.title": "Consumo y costes",
  "consumption.subtitle": "Tu consumo eléctrico y cuánto te cuesta en {name}",
  "consumption.costeMes": "Coste últimos 30 días",
  "consumption.consumoMes": "Consumo últimos 30 días",
  "consumption.precioMedio": "Precio medio",
  "consumption.efficiency": "Consumo / m²",
  "consumption.efficiencyNote": "Comparativa anual disponible con histórico completo",
  "consumption.noHistory": "Sin histórico para comparar",

  "franja.cara": "Hora cara",
  "franja.normal": "Hora normal",
  "franja.barata": "Hora barata",

  "conclusions.title": "Conclusiones automáticas",
  "conclusions.sub": "Resumen interpretativo de los datos",

  "forecast.title": "Predicción de consumo",
  "forecast.subtitle": "Predicción basada en histórico Datadis para {name}",

  "contract.title": "Contrato eléctrico",
  "contract.subtitle": "Datos del contrato eléctrico de {name}",
  "contract.id": "Identificador",
  "contract.name": "Nombre",
  "contract.cups": "Código CUPS",
  "contract.distrib": "Distribuidora (código)",
  "contract.province": "Provincia",
  "contract.muniInE": "Municipio (INE)",
  "contract.cp": "Código postal",
  "contract.address": "Dirección",
  "contract.surface": "Superficie (m²)",
  "contract.plan": "Plan contratado",

  "alarms.title": "Notificaciones y alarmas",
  "alarms.subtitle": "Eventos de los últimos 60 días — {name}",
  "alarms.none": "Sin alarmas en los últimos 60 días",
  "alarms.col.date": "Fecha",
  "alarms.col.severity": "Severidad",
  "alarms.col.type": "Tipo",
  "alarms.col.detail": "Detalle",

  "actuators.title": "Mantenimiento / Actuadores",
  "actuators.subtitle": "Control remoto de los actuadores configurados en {name}",
  "actuators.none": "Este alojamiento no tiene actuadores configurados.",
  "actuators.activate": "Activar",
  "actuators.deactivate": "Desactivar",

  "newClient.title": "Alta de nuevo cliente",
  "newClient.subtitle": "Crea un alojamiento nuevo en el sistema. Los campos con * son obligatorios.",
  "newClient.saving": "Guardando...",
  "newClient.create": "Crear cliente",

  // ─── LANDING ────────────────────────────────────────────
  "land.nav.funcionalidades": "Funcionalidades",
  "land.nav.precio": "Precio",
  "land.nav.equipo": "Equipo",
  "land.nav.faqs": "FAQs",
  "land.nav.contacto": "Contacto",
  "land.nav.blog": "Blog",
  "land.nav.acceder": "Acceder",
  "land.nav.pruebaGratuita": "Prueba gratuita",
  "land.nav.gestionReservas": "Gestión de reservas",
  "land.nav.gestionFacturacion": "Gestión de facturación",
  "land.nav.gestionHuespedes": "Gestión de huéspedes",
  "land.nav.motorReservas": "Motor de reservas",

  "land.foot.derechos": "Todos los derechos reservados.",
  "land.foot.soporteTecnico": "Soporte técnico",
  "land.foot.soporteOnline": "24/7 Soporte online",
  "land.foot.de8a14": "De 8h a 14h",
  "land.foot.infoLegal": "Información legal",
  "land.foot.politicaPriv": "Política de privacidad",
  "land.foot.politicaCookies": "Política de cookies",
  "land.foot.terminos": "Términos y condiciones",
  "land.foot.avisoLegal": "Aviso legal",
  "land.foot.masInfo": "Más información",
  "land.foot.acerca": "Acerca de EasyRural",
  "land.foot.moduloEnergia": "Módulo Energía",
  "land.foot.upcDih": "UPC + EasyRural (DIH 2024)",
  "land.foot.replica": "© 2026 EasyRural · Réplica visual con fines de demostración del Módulo Energía",

  "land.hero.si": "sí!",
  "land.hero.rural": "Rural,",
  "land.hero.digital": "Digital,",
  "land.hero.tambien": "también!",
  "land.hero.pms": "El PMS definitivo para ahorrar tiempo en la gestión de tu alojamiento rural.",
  "land.hero.intro": "Con EasyRural automatiza reservas, gestiona tu alojamiento desde cualquier lugar y olvídate del papeleo. Todo en un único programa, fácil y a tu medida.",
  "land.hero.empiezaGratis": "Empieza gratis",

  "land.home.gestionaTitle": "Gestiona tu alojamiento de forma fácil y eficiente con nuestro software para casas rurales.",
  "land.home.gestionaSub": "Con EasyRural, accedes a sistemas de gestión especializados en turismo rural que te permiten ahorrar hasta un 80% del tiempo dedicado a la administración de tu negocio.",
  "land.home.saberMas": "Saber más",
  "land.home.fn1.t": "Gestión de reservas",
  "land.home.fn1.d": "Gestiona las reservas y disponibilidad de tu alojamiento y automatiza su distribución gracias a la conectividad con los principales portales de reserva.",
  "land.home.fn2.t": "Gestión de facturación",
  "land.home.fn2.d": "Emite las facturas correspondientes a los cargos de las reservas y registra las facturas recibidas. Lleva un control minucioso de la situación económica de tu casa rural.",
  "land.home.fn3.t": "Gestión de clientes",
  "land.home.fn3.d": "Centraliza y gestiona la información de tus clientes para optimizar la interacción con ellos, aumentando su grado de fidelización y automatizando el envío de informes policiales.",

  "land.home.adaptadoTitle": "Adaptado a todo tipo de alojamientos rurales",
  "land.home.adaptadoSub": "Ya sea que gestiones un alojamiento completo o por habitaciones, nuestro programa se adapta a ti. Easyrural es un software versátil, intuitivo y escalable.",
  "land.home.alqCompleto.t": "Alquiler completo",
  "land.home.alqCompleto.d": "Gestiona reservas, disponibilidades y tarifas de tu alojamiento turístico de alquiler completo de forma visual y sencilla, gracias a un panel de control en formato de calendario mensual.",
  "land.home.alqHab.t": "Alquiler por habitaciones",
  "land.home.alqHab.d": "Registra las tipologías y el número de habitaciones de tu alojamiento turístico y gestiona sus disponibilidades, tarifas y reservas gracias a un panel de control muy intuitivo.",

  "land.home.porqueTitle": "¿Por qué EasyRural es el mejor software de gestión?",
  "land.home.porqueSub": "Tenemos muchas razones, pero aquí te damos 6 motivos clave:",
  "land.home.r1.t": "Especialización",
  "land.home.r1.d": "Utiliza una plataforma digital diseñada específicamente para los alojamientos de turismo rural, ofreciendo un servicio personalizado y de alta calidad.",
  "land.home.r2.t": "Sencillez",
  "land.home.r2.d": "Explora una plataforma fácil de usar y entender, sin importar tu nivel de experiencia en gestión de alojamientos rurales y herramientas digitales.",
  "land.home.r3.t": "Eficiencia",
  "land.home.r3.d": "Ahorra hasta un 80% del tiempo que solías invertir en la gestión de tu alojamiento, así podrás dedicar más tiempo a ti mismo y a tus huéspedes.",
  "land.home.r4.t": "Cumplimiento legal",
  "land.home.r4.d": "Cumple con las nuevas normativas españolas de facturación electrónica y registro de huéspedes, asegurando total transparencia y cumplimiento legal.",
  "land.home.r5.t": "Gestión en la nube",
  "land.home.r5.d": "Administra tu alojamiento desde cualquier dispositivo y en cualquier momento, sin necesidad de instalar nuestro programa.",
  "land.home.r6.t": "Conectividad",
  "land.home.r6.d": "Automatiza la comercialización de tu alojamiento conectando EasyRural con tus anuncios en plataformas de alquiler vacacional como Booking, Airbnb y VRBO.",

  "land.home.f1.t": "Creación y seguimiento completo de reservas",
  "land.home.f1.d": "Verifica disponibilidad en tiempo real, crea presupuestos automatizados, confirma o cancela reservas, y realiza un seguimiento de pagos y actualizaciones desde una sola plataforma.",
  "land.home.f2.t": "Tarifas y calendario bajo control",
  "land.home.f2.d": "Ajusta tus tarifas y calendario de disponibilidad de forma flexible. Configura precios según temporada, define políticas de reserva específicas y actualiza cambios en tiempo real.",
  "land.home.f3.t": "Sincronización automática con más de 100 portales",
  "land.home.f3.d": "Conecta tu alojamiento con las principales OTAs como Booking o Airbnb y automatiza la sincronización de reservas, disponibilidad y tarifas. Evita el overbooking.",
  "land.home.f4.t": "Registro digital de huéspedes con informes automatizados",
  "land.home.f4.d": "Cumple con la normativa vigente gracias al registro digital de huéspedes. Realiza el check-in online o permite que tus clientes lo hagan por sí mismos.",
  "land.home.f5.t": "Facturación integrada y sin complicaciones",
  "land.home.f5.d": "Emite facturas digitales por cada reserva y envíalas directamente a tus huéspedes. Registra todos los cargos fácilmente y lleva un control claro y organizado.",

  "land.home.visibilidadTitle": "Aumenta tu visibilidad y evita errores de reserva",
  "land.home.visibilidad1": "Con EasyRural, olvídate de los overbookings y de actualizar manualmente cada calendario. Nuestra solución para la gestión de casas rurales se conecta con más de 100 portales de reservas mediante integración iCal, lo que garantiza una sincronización automática en tiempo real de la disponibilidad de tu alojamiento.",
  "land.home.visibilidad2": "Cada vez que se registra una nueva reserva, el sistema actualiza todos los canales donde estás presente, manteniendo la información coherente y evitando errores. Así, mejoras tu visibilidad online, aumentas la confianza de los huéspedes y ofreces una experiencia de reserva fluida y profesional.",
  "land.home.portalesConect": "portales conectados",
  "land.home.entreOtros": "... ¡y entre muchos otros portales!",

  "land.banner.title": "Prueba EasyRural completamente gratis y sin límites durante 14 días.",
  "land.banner.desc": "En tan solo 10 minutos, EasyRural estará listo y funcionando en tu alojamiento turístico, sin necesidad de instalaciones complicadas.",

  "land.home.confianTitle": "Alojamientos rurales que confían en nuestro PMS:",
  "land.home.opinanTitle": "¿Qué opinan los clientes de nuestros sistemas?",
  "land.home.test1.t": "Fácil de usar y muy eficiente",
  "land.home.test1.q": "EasyRural ha simplificado la gestión de nuestro refugio de montaña. Su interfaz intuitiva facilita la gestión de reservas, facturación y registro de huéspedes. Con EasyRural, tenemos un mayor control de la asignación de camas y de los cargos de reserva.",
  "land.home.test2.t": "Ahorro de tiempo y esfuerzo",
  "land.home.test2.q": "Gracias a EasyRural, ahora puedo dedicar más tiempo a atender a mis huéspedes y mejorar su experiencia en lugar de perderlo en tareas administrativas. La automatización de procesos como la facturación y el registro de huéspedes ha sido un gran alivio.",
  "land.home.test3.t": "Sencillez para principiantes",
  "land.home.test3.q": "Como propietario de un alojamiento rural sin experiencia previa en herramientas digitales, EasyRural ha sido un gran descubrimiento. La plataforma es increíblemente fácil de usar, con instrucciones claras y funciones intuitivas.",
  "land.home.test4.t": "¡Soporte técnico excepcional!",
  "land.home.test4.q": "Lo que más me impresionó de EasyRural fue el excelente servicio de atención al cliente. Cada vez que he tenido una pregunta o un problema, el equipo de soporte ha respondido de manera rápida y efectiva.",

  // Login/Register en la landing
  "land.login.title": "Iniciar sesión",
  "land.login.email": "Correo electrónico",
  "land.login.emailPh": "Correo electrónico...",
  "land.login.password": "Contraseña",
  "land.login.passwordPh": "Contraseña...",
  "land.login.err": "Email o contraseña incorrectos",
  "land.login.btn": "Continúa con correo electrónico",
  "land.login.terms1": "Al iniciar sesión en EasyRural, aceptas nuestras",
  "land.login.terms2": "de contratación y nuestra",
  "land.login.privacy": "política de privacidad",
  "land.login.noAccount": "¿No tienes cuenta?",
  "land.login.createAccount": "Crear cuenta",

  "land.reg.title": "Crear cuenta de usuario",
  "land.reg.name": "Nombre",
  "land.reg.namePh": "Nombre...",
  "land.reg.passwordHelp": "Crea una contraseña segura con un mínimo de 8 dígitos, que incluya como mínimo una mayúscula, una minúscula, un número y un símbolo.",
  "land.reg.confirm": "Confirma la contraseña",
  "land.reg.confirmPh": "Confirmar contraseña...",
  "land.reg.passMismatch": "Las contraseñas no coinciden",
  "land.reg.passShort": "La contraseña debe tener al menos 8 caracteres",
  "land.reg.sent": "Solicitud preparada. Te contactaremos pronto.",
  "land.reg.btn": "Continúa con correo electrónico",
  "land.reg.terms1": "Al registrarte en EasyRural, aceptas nuestras",
  "land.reg.haveAccount": "¿Ya tienes cuenta?",
  "land.reg.login": "Iniciar sesión",

  // ─── PRECIOS ─────────
  "land.pre.title": "Comienza tu periodo de prueba gratuito hoy mismo y utiliza el cupón",
  "land.pre.welcome": "WELCOME50",
  "land.pre.titleEnd": "para obtener un descuento del 50% durante 3 meses.",
  "land.pre.intro": "Prueba EasyRural de forma gratuita durante 14 días y experimenta la comodidad de gestionar eficientemente tu alojamiento rural.",
  "land.pre.tipo": "Indica si tu alojamiento turístico es de alquiler completo o por habitaciones.",
  "land.pre.completo": "Completo",
  "land.pre.habitaciones": "Por habitaciones",
  "land.pre.planBasico": "Rural Básico",
  "land.pre.planBasicoSub": "Facilita y automatiza los procesos esenciales para la gestión de tu alojamiento de turismo rural.",
  "land.pre.planRoomsSub": "Gestiona alojamientos por habitaciones con tarifas escalables según tu volumen.",
  "land.pre.desde": "Desde",
  "land.pre.mes": "€/mes",
  "land.pre.anio": "€/año",
  "land.pre.durante3": "Durante 3 meses",
  "land.pre.descuento15": "Descuento 15%",
  "land.pre.kitDigital": "* Subvencionable con el Kit Digital",
  "land.pre.feat1": "Sistema de reservas (PMS)",
  "land.pre.feat2": "Gestión de la relación con los huéspedes (CRM)",
  "land.pre.feat3": "Elaboración de informes policiales",
  "land.pre.feat4": "Control de caja y facturación",
  "land.pre.feat5": "Elaboración de informes financieros",
  "land.pre.feat6": "1 usuario",
  "land.pre.feat7": "4 idiomas disponibles",
  "land.pre.feat8": "Soporte los 365 días del año",
  "land.pre.feat9": "10 habitaciones incluidas",
  "land.pre.feat10": "2€ extra cada 10 habitaciones (Máx. 60)",
  "land.pre.empresasTitle": "¿Eres una empresa que gestiona más de 5 alojamientos turísticos?",
  "land.pre.empresasDesc": "En EasyRural ofrecemos tarifas especiales y personalizadas que se adaptan a tu volumen de negocio. Obtén acceso completo a todas nuestras funcionalidades a un precio exclusivo diseñado para ti.",
  "land.pre.contactanos": "Contáctanos",

  // ─── EQUIPO ─────────
  "land.eq.label": "Equipo",
  "land.eq.h1": "Digitalización, eficiencia y simplicidad",
  "land.eq.intro1": "EasyRural llega para romper barreras y llevar la revolución digital a los alojamientos de turismo rural.",
  "land.eq.intro2": "¡Basta de tecnologías complicadas e inaccesibles para los profesionales del turismo rural! Nacemos con el objetivo de liberar el potencial de la digitalización en los alojamientos rurales.",
  "land.eq.misionTit": "Nuestra misión",
  "land.eq.misionDesc": "Nuestra misión es modernizar el mundo rural a través de nuestro ecosistema digital.",
  "land.eq.visionTit": "Nuestra visión",
  "land.eq.visionDesc": "No pararemos hasta ser la empresa líder en la transformación digital del turismo rural. Dedicamos todos nuestros esfuerzos a ofrecer una solución integral, flexible y adaptada a las necesidades de los profesionales del turismo rural.",
  "land.eq.valoresTit": "Nuestros valores",
  "land.eq.v1.t": "Innovación",
  "land.eq.v1.d": "Nuestra cultura empresarial nos sitúa en la vanguardia, buscando y creando soluciones diferentes y únicas para los alojamientos de turismo rural.",
  "land.eq.v2.t": "Eficiencia",
  "land.eq.v2.d": "Todos nuestros procesos están enfocados en la optimización del tiempo y los resultados. La eficiencia para nosotros es una razón de ser.",
  "land.eq.v3.t": "Pasión por el cliente",
  "land.eq.v3.d": "EasyRural no tiene sentido sin las personas que confían en ella. Por eso, cada paso lo damos poniéndolas en el centro de nuestra actividad.",
  "land.eq.v4.t": "Especialización",
  "land.eq.v4.d": "Estamos comprometidos a ofrecer una solución digital adaptada a las necesidades de los profesionales del turismo rural, brindando un servicio de calidad y personalizado.",
  "land.eq.v5.t": "Sostenibilidad",
  "land.eq.v5.d": "Nos comprometemos a que todas las acciones relacionadas con nuestra actividad sean respetuosas con el medio ambiente y con la sociedad.",
  "land.eq.detrasTit": "¿Quién está detrás de EasyRural?",
  "land.eq.m1.role": "CEO",
  "land.eq.m1.bio": "Profesional del turismo con más de 7 años de experiencia en la gestión de alojamientos turísticos. Especializado en el desarrollo de proyectos relacionados con el turismo rural.",
  "land.eq.m2.role": "CTO",
  "land.eq.m2.bio": "Desarrollador de aplicaciones informáticas y director de proyectos en una gran variedad de aplicaciones empresariales, como Clockio. Socio fundador de Avantiam.",

  // ─── FAQS ─────────
  "land.faq.title": "PREGUNTAS FRECUENTES",
  "land.faq.q1": "¿EasyRural ofrece alguna prueba gratuita o demostración?",
  "land.faq.a1": "Sí, ofrecemos una prueba gratuita de 14 días para que puedas explorar todas las características y funcionalidades antes de tomar una decisión.",
  "land.faq.q2": "¿Se puede utilizar EasyRural desde cualquier dispositivo?",
  "land.faq.a2": "Sí, EasyRural tiene una plataforma web con un diseño adaptativo para ordenadores, tabletas y teléfonos móviles.",
  "land.faq.q3": "¿Necesito experiencia previa en herramientas digitales para utilizar EasyRural?",
  "land.faq.a3": "No, EasyRural está diseñado pensando en usuarios de todos los niveles. La plataforma tiene una interfaz intuitiva y ofrece instrucciones claras, accesible incluso para nuevos en el mundo digital.",
  "land.faq.q4": "¿Tiene EasyRural la opción de registrar más de un alojamiento?",
  "land.faq.a4": "EasyRural permite conectar diferentes alojamientos a un solo usuario. Al iniciar sesión seleccionas el alojamiento que quieres gestionar y puedes cambiar en cualquier momento desde el menú superior.",
  "land.faq.q5": "¿Cómo puedo contactar con EasyRural si tengo dudas?",
  "land.faq.a5": "Puedes contactar por correo electrónico a info@easyrural.net (Soporte 24/7) o llamando al +34 649 13 35 41 (lunes a viernes 8h-14h). También en nuestro Discord para soporte por videollamada.",
  "land.faq.q6": "¿Puedo conectar mi alojamiento con los portales de reserva vacacional?",
  "land.faq.a6": "Estamos trabajando activamente en la mejora de la conectividad con Booking, Airbnb, VRBO y Expedia mediante API. Implementaremos el sistema iCal para más de 100 portales.",
  "land.faq.q7": "¿Es posible personalizar las tarifas según la demanda y la temporada?",
  "land.faq.a7": "Sí, EasyRural te da la flexibilidad de ajustar las tarifas según demanda y temporada. Establece diferentes precios para alta/baja demanda y descuentos especiales.",
  "land.faq.q8": "¿Puedo personalizar las políticas de reserva en EasyRural?",
  "land.faq.a8": "Sí, EasyRural permite personalizar las políticas según las necesidades de tu alojamiento: reglas de cancelación, políticas de depósito y requisitos específicos.",
  "land.faq.q9": "¿El sistema genera informes policiales automatizados?",
  "land.faq.a9": "Sí, EasyRural crea informes policiales automáticamente a partir de la información registrada durante la entrada de los huéspedes.",
  "land.faq.q10": "¿Cómo se realiza la facturación de las reservas?",
  "land.faq.a10": "Nuestro sistema te permite facturar todos los cargos de una reserva y crear una factura digital rápidamente. Las envías directamente a tus huéspedes por correo.",
  "land.faq.q11": "¿Puedo registrar y administrar las facturas de proveedores?",
  "land.faq.a11": "Sí, la herramienta permite registrar y administrar facturas de proveedores. Facilita la conciliación con pagos y contribuye al control eficiente de gastos.",
  "land.faq.q12": "¿Cómo me ayuda la herramienta con el estado de caja?",
  "land.faq.a12": "EasyRural te proporciona seguimiento actualizado en tiempo real del estado de caja, con visión de los flujos por metodología de pago.",
  "land.faq.q13": "¿Puedo generar informes financieros?",
  "land.faq.a13": "Sí, generas informes financieros con visión detallada de ingresos y gastos. También descargas resúmenes de impuestos para tu gestor.",
  "land.faq.q14": "¿Cómo mejorar la experiencia de mis huéspedes con la herramienta?",
  "land.faq.a14": "El CRM centraliza toda la información de huéspedes. Personalizas interacciones, anticipas necesidades, fomentas fidelidad y aumentas la satisfacción.",
  "land.faq.q15": "¿Cuáles son los beneficios de registrar proveedores?",
  "land.faq.a15": "Simplifica la introducción de facturas, minimiza errores al automatizar la recuperación de datos y te permite disponer de un historial por proveedor.",
  "land.faq.q16": "¿Es seguro almacenar la información de mis huéspedes en EasyRural?",
  "land.faq.a16": "Sí, EasyRural cumple con la normativa europea de protección de datos (GDPR) y aplica medidas de seguridad para proteger información sensible.",

  // ─── CONTACTO ─────────
  "land.ct.formTitle": "Formulario de contacto",
  "land.ct.alsoTitle": "Contáctanos también",
  "land.ct.fName": "Nombre completo *",
  "land.ct.fEmail": "Email *",
  "land.ct.fPhone": "Teléfono",
  "land.ct.fSubject": "Asunto *",
  "land.ct.fMessage": "Mensaje *",
  "land.ct.fAccept": "He leído y acepto la",
  "land.ct.fPrivacy": "POLÍTICA DE PRIVACIDAD",
  "land.ct.fSubmit": "Enviar mensaje",
  "land.ct.sent": "Mensaje preparado en tu cliente de correo.",
  "land.ct.byEmail": "Email",
  "land.ct.byPhone": "Teléfono",
  "land.ct.byDiscord": "Discord",
  "land.ct.discordCta": "Únete al servidor",
  "land.ct.discordSub": "Soporte por videollamada",

  // ─── BLOG ─────────
  "land.bl.title": "Blog EasyRural",
  "land.bl.subtitle": "Consejos, novedades y casos de éxito para gestores de alojamientos rurales.",
  "land.bl.read": "Leer artículo →",
  "land.bl.p1.t": "5 razones para digitalizar tu casa rural en 2026",
  "land.bl.p1.d": "Descubre por qué la digitalización es clave para mantenerte competitivo y mejorar la experiencia de tus huéspedes.",
  "land.bl.p1.date": "12 ene 2026",
  "land.bl.p2.t": "Registro único de alquiler turístico: lo que debes saber",
  "land.bl.p2.d": "Una guía sobre el nuevo registro obligatorio para alquileres turísticos en España.",
  "land.bl.p2.date": "5 ene 2026",
  "land.bl.p3.t": "Sincronización iCal: la clave para evitar overbookings",
  "land.bl.p3.d": "Cómo mantener tu calendario actualizado en Booking, Airbnb y otras 100 plataformas con un único sistema.",
  "land.bl.p3.date": "20 dic 2025",

  // ─── FUNCIONALIDADES (4 subpáginas) ─────────
  "land.fn.queIncluye": "¿Qué incluye?",
  "land.fn.empiezaGratis": "Empieza gratis",

  "land.fn.reservas.hero": "Centraliza y automatiza las reservas de tu casa rural en una única plataforma.",
  "land.fn.reservas.desc": "Verifica disponibilidad en tiempo real, crea presupuestos automatizados, confirma o cancela reservas y realiza un seguimiento de pagos y actualizaciones desde una sola plataforma.",
  "land.fn.reservas.f1.t": "Creación y seguimiento completo de reservas",
  "land.fn.reservas.f1.d": "Verifica disponibilidad en tiempo real, crea presupuestos automatizados, confirma o cancela reservas, y realiza un seguimiento de pagos y actualizaciones.",
  "land.fn.reservas.f2.t": "Tarifas y calendario bajo control",
  "land.fn.reservas.f2.d": "Ajusta tarifas y calendario de forma flexible. Configura precios según temporada y políticas específicas en tiempo real.",
  "land.fn.reservas.f3.t": "Sincronización automática con más de 100 portales",
  "land.fn.reservas.f3.d": "Conecta con OTAs como Booking o Airbnb y automatiza la sincronización. Evita el overbooking.",
  "land.fn.reservas.f4.t": "Registro digital de huéspedes",
  "land.fn.reservas.f4.d": "Cumple con la normativa con registro digital. Check-in online o que lo hagan los clientes.",
  "land.fn.reservas.f5.t": "Facturación integrada",
  "land.fn.reservas.f5.d": "Facturas digitales por cada reserva enviadas directamente a tus huéspedes.",

  "land.fn.facturacion.hero": "Factura electrónica y control financiero para tu casa rural sin dolores de cabeza.",
  "land.fn.facturacion.desc": "Emite facturas digitales por cada reserva, registra las recibidas y obtén informes financieros automatizados para cumplir con la nueva normativa española.",
  "land.fn.facturacion.f1.t": "Facturación digital por reserva",
  "land.fn.facturacion.f1.d": "Emite facturas digitales en segundos y envíalas automáticamente a tus huéspedes.",
  "land.fn.facturacion.f2.t": "Registro de facturas recibidas",
  "land.fn.facturacion.f2.d": "Lleva el control de facturas de proveedores con conciliación con pagos.",
  "land.fn.facturacion.f3.t": "Control de caja en tiempo real",
  "land.fn.facturacion.f3.d": "Visualiza el estado de caja por método de pago en cualquier momento.",
  "land.fn.facturacion.f4.t": "Informes financieros automáticos",
  "land.fn.facturacion.f4.d": "Informes detallados de ingresos y gastos. Resúmenes para tu gestor.",
  "land.fn.facturacion.f5.t": "Cumplimiento Verifactu / Factura-e",
  "land.fn.facturacion.f5.d": "Cumple con la nueva normativa española de facturación electrónica sin esfuerzo.",

  "land.fn.huespedes.hero": "Tu CRM hotelero adaptado al turismo rural.",
  "land.fn.huespedes.desc": "Centraliza datos de huéspedes, automatiza informes policiales y fideliza con interacciones personalizadas.",
  "land.fn.huespedes.f1.t": "CRM centralizado",
  "land.fn.huespedes.f1.d": "Toda la información de huéspedes en un único lugar. Histórico de estancias y preferencias.",
  "land.fn.huespedes.f2.t": "Check-in online",
  "land.fn.huespedes.f2.d": "Permite a tus huéspedes hacer check-in desde su móvil antes de llegar.",
  "land.fn.huespedes.f3.t": "Informes policiales automatizados",
  "land.fn.huespedes.f3.d": "Genera y envía partes de viajeros a las autoridades sin papeleos.",
  "land.fn.huespedes.f4.t": "Fidelización y comunicación",
  "land.fn.huespedes.f4.d": "Personaliza interacciones, anticipa necesidades y aumenta satisfacción.",
  "land.fn.huespedes.f5.t": "Cumplimiento GDPR",
  "land.fn.huespedes.f5.d": "Almacena datos siguiendo la normativa europea de protección de datos.",

  "land.fn.motor.hero": "Recibe reservas directas desde tu web sin comisiones.",
  "land.fn.motor.desc": "Un motor de reservas integrado en tu propia web que evita comisiones de portales.",
  "land.fn.motor.f1.t": "Reservas directas sin comisiones",
  "land.fn.motor.f1.d": "Tus huéspedes reservan desde tu web. Cero comisión de OTA.",
  "land.fn.motor.f2.t": "Diseño responsive",
  "land.fn.motor.f2.d": "Integración visual que se adapta a tu marca y a móvil, tablet u ordenador.",
  "land.fn.motor.f3.t": "Disponibilidad y tarifas sincronizadas",
  "land.fn.motor.f3.d": "Lee la disponibilidad de tu PMS — nunca venderás de más.",
  "land.fn.motor.f4.t": "Pago online seguro",
  "land.fn.motor.f4.d": "Acepta tarjeta y registra anticipos siguiendo el flujo del PMS.",
  "land.fn.motor.f5.t": "Multi-idioma y multi-divisa",
  "land.fn.motor.f5.d": "Llega a clientes internacionales en ES, CA, EN y FR.",

  // ─── PANEL ADMIN / MULTI-USUARIO (v9.1) ────────────────
  "nav.adminOverview": "Panel admin",
  "nav.users": "Usuarios",

  "admin.title": "Panel de administración",
  "admin.subtitle": "Vista rápida de todos los alojamientos activos en EasyRural.",
  "admin.newClient": "+ Nuevo alojamiento",
  "admin.kpi.active": "Activos",
  "admin.kpi.activeSub": "en {n} municipio{s}",
  "admin.kpi.drafts": "Borradores",
  "admin.kpi.draftsSub": "Sin plan contratado",
  "admin.kpi.complete": "Plan completo",
  "admin.kpi.completeSub": "APIs + Sensores IoT",
  "admin.kpi.basic": "Plan básico",
  "admin.kpi.basicSub": "Solo APIs externas",
  "admin.mapTitle": "MAPA GLOBAL",
  "admin.listTitle": "ALOJAMIENTOS",
  "admin.noHousings": "Aún no hay alojamientos dados de alta.",

  "card.today": "Hoy",
  "card.month": "Mes",
  "card.costMonth": "Coste mes",
  "card.powerNow": "Potencia ahora",
  "card.openDash": "Abrir dashboard →",
  "card.completeData": "Completar datos →",
  "card.draftNote": "Registrado en la plataforma pero sin plan contratado. Falta CUPS y/o instalación de sensores.",
  "card.status.ok": "OK",
  "card.status.draft": "Borrador",
  "card.status.alarm": "{n} alarma{s}",
  "card.plan.none": "sin plan",

  "map.globalTitle": "Mapa global de alojamientos",
  "map.title": "Mapa de alojamientos",
  "map.globalSubtitle": "Todos los alojamientos registrados en EasyRural — activos y en borrador ({n})",
  "map.mineSubtitle": "Tus alojamientos en el mapa ({n})",
  "map.badge.draft": "⚠ En borrador · sin plan",
  "map.badge.plan": "Plan {plan}",
  "map.noCoords": "No hay alojamientos con coordenadas configuradas.",

  "empty.adminTitle": "Aún no hay alojamientos",
  "empty.clientTitle": "Hola, {name}",
  "empty.clientTitleFallback": "Hola, bienvenido",
  "empty.adminBody": "Todavía no se ha dado de alta ningún alojamiento en la plataforma. Empieza dando de alta el primer cliente y sus dispositivos aparecerán aquí automáticamente.",
  "empty.clientBody": "Tu alojamiento aún no está activo en la plataforma. En cuanto el equipo de EasyRural termine la configuración de tus sensores, verás aquí tu consumo, huella de carbono y alarmas en tiempo real.",
  "empty.firstClient": "+ Dar de alta primer cliente",
  "empty.goMap": "Ver mapa de servicios",
  "empty.contactHint": "¿Crees que es un error? Contacta a",

  "users.title": "Usuarios y accesos",
  "users.subtitle": "Cuentas activas en la demo y qué alojamiento puede ver cada una.",
  "users.kpi.total": "Usuarios",
  "users.kpi.totalSub": "en la plataforma",
  "users.kpi.admin": "Administradores",
  "users.kpi.adminSub": "Ven todos los alojamientos",
  "users.kpi.multi": "Multi-alojamiento",
  "users.kpi.multiSub": "Dueños de ≥2 casas",
  "users.kpi.single": "Individuales",
  "users.kpi.singleSub": "Una única casa",
  "users.col.name": "Nombre",
  "users.col.email": "Email",
  "users.col.role": "Rol",
  "users.col.access": "Alojamientos con acceso",
  "users.col.password": "Contraseña demo",
  "users.role.admin": "Administrador",
  "users.role.cliente": "Cliente",
  "users.role.adminFull": "Admin global",
  "users.role.multi": "Multi-owner",
  "users.access.all": "Todos (incluye borradores)",
  "users.demoNoteTitle": "⚠ Solo demo",
  "users.demoNote": "estas cuentas están hard-coded en el frontend para la presentación. En producción se sustituyen por autenticación real (JWT + hash de contraseña en backend).",

  "edit.title": "Editar {name}",
  "edit.subtitle": "Completa o actualiza los datos del alojamiento.",
  "edit.saveChanges": "Guardar cambios",
  "edit.saveDraft": "Guardar como borrador",

  "newClient.draftToggle": "Guardar como borrador",
  "newClient.draftHelp": "El alojamiento queda registrado en EasyRural (visible en el mapa) pero SIN plan contratado ni CUPS. Se puede completar más adelante.",
  "newClient.saveDraft": "Guardar borrador",
};

const CA = {
  "common.loading": "Carregant…",
  "common.error": "Error",
  "common.retry": "Reintentar",
  "common.noData": "Sense dades",
  "common.connected": "Connectat",
  "common.waitingSensors": "Esperant dades de sensors…",
  "common.waiting": "Esperant dades…",
  "common.live": "EN DIRECTE",
  "common.backHome": "← Inici",

  "nav.status": "Estat sensors",
  "nav.map": "Mapa allotjaments",
  "nav.dashboard": "Quadre de comandament",
  "nav.consumption": "Consum i costos",
  "nav.forecast": "Predicció",
  "nav.contract": "Contracte elèctric",
  "nav.savings": "Estalvi",
  "nav.alarms": "Notificacions",
  "nav.actuators": "Manteniment",
  "nav.install": "Instal·lació",
  "nav.sec.Gestión": "Gestió",
  "nav.sec.Alojamiento": "Allotjament",
  "nav.sec.Equipos": "Equips",
  "nav.newClient": "Alta client",
  "nav.agenda": "Agenda",
  "nav.guests": "Hostes",
  "nav.access": "Accessos",
  "nav.casaRural": "Casa Rural",
  "nav.version": "Versió 9.0 (live)",

  "login.title": "Panell d'administració",
  "login.passwordLabel": "Contrasenya",
  "login.submit": "Entrar",
  "login.error": "Contrasenya incorrecta",

  "header.logout": "Sortir",
  "header.adminPanel": "Panell admin",

  "footer.copyright": "© 2026 EasyRural · Mòdul Energia · UPC + EasyRural (DIH 2024)",
  "footer.support": "Suport",

  "status.title": "Estat de l'allotjament",
  "status.subtitle": "Serveis actius a {name}",
  "status.plan": "Pla",
  "status.activeServices": "Serveis actius",
  "status.installedDevices": "Dispositius instal·lats",
  "status.devicesUnit": "dispositius",
  "status.alarms7d": "Alarmes (7D)",
  "status.alarmsUnit": "esdeveniments",
  "status.availability30d": "Disponibilitat 30 dies",
  "status.notInstalled": "No instal·lat",
  "status.noRecent": "Sense lectures recents",
  "status.deviceSingle": "dispositiu",
  "status.devicePlural": "dispositius",
  "status.actuator": "actuador",
  "status.actuatorPlural": "actuadors",
  "status.sensors.temp": "Sensors de temperatura",
  "status.sensors.electric": "Sensors elèctrics",
  "status.sensors.water": "Sensors d'aigua",
  "status.sensors.consumption": "Consum elèctric",
  "status.sensors.meteo": "Meteorologia",
  "status.sensors.price": "Preu de la llum",
  "status.sensors.co2": "Petjada de carboni",
  "status.sensors.alarms": "Alarmes i avisos",
  "status.sensors.control": "Control remot",
  "status.sensors.connection": "Connexió sensors",
  "status.calc": "Càlcul automàtic",
  "status.botTelegram": "Bot Telegram",
  "status.valve": "Vàlvula automàtica",
  "status.localServer": "Servidor local",

  "map.title": "Mapa d'allotjaments",
  "map.subtitle": "Distribució geogràfica dels allotjaments gestionats",

  "dashboard.title": "Quadre de comandament",
  "dashboard.subtitle": "Resum en temps real de l'allotjament",

  "pvpc.cheapest": "MÉS BARATA",
  "pvpc.now": "ARA",
  "pvpc.expensive": "MÉS CARA",
  "pvpc.cleanest": "MÉS NETA",
  "pvpc.dirtiest": "MÉS BRUTA",

  "consumption.title": "Consum i costos",
  "consumption.subtitle": "El teu consum elèctric i quant et costa a {name}",
  "consumption.costeMes": "Cost últims 30 dies",
  "consumption.consumoMes": "Consum últims 30 dies",
  "consumption.precioMedio": "Preu mitjà",
  "consumption.efficiency": "Consum / m²",
  "consumption.efficiencyNote": "Comparativa anual disponible amb històric complet",
  "consumption.noHistory": "Sense històric per comparar",

  "franja.cara": "Hora cara",
  "franja.normal": "Hora normal",
  "franja.barata": "Hora barata",

  "conclusions.title": "Conclusions automàtiques",
  "conclusions.sub": "Resum interpretatiu de les dades",

  "forecast.title": "Predicció de consum",
  "forecast.subtitle": "Predicció basada en històric Datadis per a {name}",

  "contract.title": "Contracte elèctric",
  "contract.subtitle": "Dades del contracte elèctric de {name}",
  "contract.id": "Identificador",
  "contract.name": "Nom",
  "contract.cups": "Codi CUPS",
  "contract.distrib": "Distribuïdora (codi)",
  "contract.province": "Província",
  "contract.muniInE": "Municipi (INE)",
  "contract.cp": "Codi postal",
  "contract.address": "Adreça",
  "contract.surface": "Superfície (m²)",
  "contract.plan": "Pla contractat",

  "alarms.title": "Notificacions i alarmes",
  "alarms.subtitle": "Esdeveniments dels darrers 60 dies — {name}",
  "alarms.none": "Sense alarmes en els darrers 60 dies",
  "alarms.col.date": "Data",
  "alarms.col.severity": "Severitat",
  "alarms.col.type": "Tipus",
  "alarms.col.detail": "Detall",

  "actuators.title": "Manteniment / Actuadors",
  "actuators.subtitle": "Control remot dels actuadors configurats a {name}",
  "actuators.none": "Aquest allotjament no té actuadors configurats.",
  "actuators.activate": "Activar",
  "actuators.deactivate": "Desactivar",

  "newClient.title": "Alta de nou client",
  "newClient.subtitle": "Crea un allotjament nou al sistema. Els camps amb * són obligatoris.",
  "newClient.saving": "Desant...",
  "newClient.create": "Crear client",

  // ─── LANDING CA ─────────────────────────────────────────
  "land.nav.funcionalidades": "Funcionalitats",
  "land.nav.precio": "Preu",
  "land.nav.equipo": "Equip",
  "land.nav.faqs": "FAQs",
  "land.nav.contacto": "Contacte",
  "land.nav.blog": "Blog",
  "land.nav.acceder": "Accedir",
  "land.nav.pruebaGratuita": "Prova gratuïta",
  "land.nav.gestionReservas": "Gestió de reserves",
  "land.nav.gestionFacturacion": "Gestió de facturació",
  "land.nav.gestionHuespedes": "Gestió d'hostes",
  "land.nav.motorReservas": "Motor de reserves",

  "land.foot.derechos": "Tots els drets reservats.",
  "land.foot.soporteTecnico": "Suport tècnic",
  "land.foot.soporteOnline": "24/7 Suport en línia",
  "land.foot.de8a14": "De 8h a 14h",
  "land.foot.infoLegal": "Informació legal",
  "land.foot.politicaPriv": "Política de privacitat",
  "land.foot.politicaCookies": "Política de cookies",
  "land.foot.terminos": "Termes i condicions",
  "land.foot.avisoLegal": "Avís legal",
  "land.foot.masInfo": "Més informació",
  "land.foot.acerca": "Sobre EasyRural",
  "land.foot.moduloEnergia": "Mòdul Energia",
  "land.foot.upcDih": "UPC + EasyRural (DIH 2024)",
  "land.foot.replica": "© 2026 EasyRural · Rèplica visual amb finalitats de demostració del Mòdul Energia",

  "land.hero.si": "sí!",
  "land.hero.rural": "Rural,",
  "land.hero.digital": "Digital,",
  "land.hero.tambien": "també!",
  "land.hero.pms": "El PMS definitiu per estalviar temps en la gestió del teu allotjament rural.",
  "land.hero.intro": "Amb EasyRural automatitza reserves, gestiona el teu allotjament des de qualsevol lloc i oblida't del paperam. Tot en un únic programa, fàcil i a la teva mida.",
  "land.hero.empiezaGratis": "Comença gratis",

  "land.home.gestionaTitle": "Gestiona el teu allotjament de forma fàcil i eficient amb el nostre programari per a cases rurals.",
  "land.home.gestionaSub": "Amb EasyRural, accedeixes a sistemes de gestió especialitzats en turisme rural que et permeten estalviar fins a un 80% del temps dedicat a l'administració del teu negoci.",
  "land.home.saberMas": "Saber-ne més",
  "land.home.fn1.t": "Gestió de reserves",
  "land.home.fn1.d": "Gestiona les reserves i disponibilitat del teu allotjament i automatitza la seva distribució gràcies a la connectivitat amb els principals portals de reserva.",
  "land.home.fn2.t": "Gestió de facturació",
  "land.home.fn2.d": "Emet les factures corresponents als càrrecs de les reserves i registra les factures rebudes. Porta un control minuciós de la situació econòmica de la teva casa rural.",
  "land.home.fn3.t": "Gestió de clients",
  "land.home.fn3.d": "Centralitza i gestiona la informació dels teus clients per optimitzar la interacció amb ells, augmentant la fidelització i automatitzant l'enviament d'informes policials.",

  "land.home.adaptadoTitle": "Adaptat a tot tipus d'allotjaments rurals",
  "land.home.adaptadoSub": "Tant si gestiones un allotjament complet com per habitacions, el nostre programa s'adapta a tu. Easyrural és un programari versàtil, intuïtiu i escalable.",
  "land.home.alqCompleto.t": "Lloguer complet",
  "land.home.alqCompleto.d": "Gestiona reserves, disponibilitats i tarifes del teu allotjament turístic de lloguer complet de forma visual i senzilla, gràcies a un panell de control en format de calendari mensual.",
  "land.home.alqHab.t": "Lloguer per habitacions",
  "land.home.alqHab.d": "Registra les tipologies i el nombre d'habitacions del teu allotjament turístic i gestiona les seves disponibilitats, tarifes i reserves gràcies a un panell de control molt intuïtiu.",

  "land.home.porqueTitle": "Per què EasyRural és el millor programari de gestió?",
  "land.home.porqueSub": "Tenim moltes raons, però aquí et donem 6 motius clau:",
  "land.home.r1.t": "Especialització",
  "land.home.r1.d": "Utilitza una plataforma digital dissenyada específicament per als allotjaments de turisme rural, oferint un servei personalitzat i d'alta qualitat.",
  "land.home.r2.t": "Senzillesa",
  "land.home.r2.d": "Explora una plataforma fàcil d'utilitzar i entendre, sense importar el teu nivell d'experiència en gestió d'allotjaments rurals i eines digitals.",
  "land.home.r3.t": "Eficiència",
  "land.home.r3.d": "Estalvia fins a un 80% del temps que solies invertir en la gestió del teu allotjament, així podràs dedicar més temps a tu mateix i als teus hostes.",
  "land.home.r4.t": "Compliment legal",
  "land.home.r4.d": "Compleix amb les noves normatives espanyoles de facturació electrònica i registre d'hostes, assegurant total transparència i compliment legal.",
  "land.home.r5.t": "Gestió al núvol",
  "land.home.r5.d": "Administra el teu allotjament des de qualsevol dispositiu i en qualsevol moment, sense necessitat d'instal·lar el nostre programa.",
  "land.home.r6.t": "Connectivitat",
  "land.home.r6.d": "Automatitza la comercialització del teu allotjament connectant EasyRural amb els teus anuncis a plataformes de lloguer vacacional com Booking, Airbnb i VRBO.",

  "land.home.f1.t": "Creació i seguiment complet de reserves",
  "land.home.f1.d": "Verifica disponibilitat en temps real, crea pressupostos automatitzats, confirma o cancel·la reserves i fes un seguiment de pagaments i actualitzacions des d'una sola plataforma.",
  "land.home.f2.t": "Tarifes i calendari sota control",
  "land.home.f2.d": "Ajusta les teves tarifes i calendari de disponibilitat de forma flexible. Configura preus segons temporada, defineix polítiques de reserva específiques i actualitza canvis en temps real.",
  "land.home.f3.t": "Sincronització automàtica amb més de 100 portals",
  "land.home.f3.d": "Connecta el teu allotjament amb les principals OTAs com Booking o Airbnb i automatitza la sincronització de reserves, disponibilitat i tarifes. Evita l'overbooking.",
  "land.home.f4.t": "Registre digital d'hostes amb informes automatitzats",
  "land.home.f4.d": "Compleix amb la normativa vigent gràcies al registre digital d'hostes. Fes el check-in en línia o permet que els teus clients ho facin per si mateixos.",
  "land.home.f5.t": "Facturació integrada i sense complicacions",
  "land.home.f5.d": "Emet factures digitals per cada reserva i envia-les directament als teus hostes. Registra tots els càrrecs fàcilment i porta un control clar i organitzat.",

  "land.home.visibilidadTitle": "Augmenta la teva visibilitat i evita errors de reserva",
  "land.home.visibilidad1": "Amb EasyRural, oblida't dels overbookings i d'actualitzar manualment cada calendari. La nostra solució per a la gestió de cases rurals es connecta amb més de 100 portals de reserves mitjançant integració iCal.",
  "land.home.visibilidad2": "Cada vegada que es registra una nova reserva, el sistema actualitza tots els canals on ets present, mantenint la informació coherent i evitant errors.",
  "land.home.portalesConect": "portals connectats",
  "land.home.entreOtros": "... i entre molts altres portals!",

  "land.banner.title": "Prova EasyRural completament gratis i sense límits durant 14 dies.",
  "land.banner.desc": "En només 10 minuts, EasyRural estarà llest i funcionant al teu allotjament turístic, sense necessitat d'instal·lacions complicades.",

  "land.home.confianTitle": "Allotjaments rurals que confien en el nostre PMS:",
  "land.home.opinanTitle": "Què opinen els clients dels nostres sistemes?",
  "land.home.test1.t": "Fàcil d'utilitzar i molt eficient",
  "land.home.test1.q": "EasyRural ha simplificat la gestió del nostre refugi de muntanya. La seva interfície intuïtiva facilita la gestió de reserves, facturació i registre d'hostes.",
  "land.home.test2.t": "Estalvi de temps i esforç",
  "land.home.test2.q": "Gràcies a EasyRural, ara puc dedicar més temps a atendre els meus hostes i millorar la seva experiència en lloc de perdre'l en tasques administratives.",
  "land.home.test3.t": "Senzillesa per a principiants",
  "land.home.test3.q": "Com a propietari d'un allotjament rural sense experiència prèvia en eines digitals, EasyRural ha estat un gran descobriment. La plataforma és increïblement fàcil d'utilitzar.",
  "land.home.test4.t": "Suport tècnic excepcional!",
  "land.home.test4.q": "El que més em va impressionar d'EasyRural va ser l'excel·lent servei d'atenció al client. Cada vegada que he tingut una pregunta o un problema, l'equip de suport ha respost de manera ràpida i efectiva.",

  "land.login.title": "Iniciar sessió",
  "land.login.email": "Correu electrònic",
  "land.login.emailPh": "Correu electrònic...",
  "land.login.password": "Contrasenya",
  "land.login.passwordPh": "Contrasenya...",
  "land.login.err": "Email o contrasenya incorrectes",
  "land.login.btn": "Continua amb correu electrònic",
  "land.login.terms1": "En iniciar sessió a EasyRural, acceptes els nostres",
  "land.login.terms2": "de contractació i la nostra",
  "land.login.privacy": "política de privacitat",
  "land.login.noAccount": "No tens compte?",
  "land.login.createAccount": "Crear compte",

  "land.reg.title": "Crear compte d'usuari",
  "land.reg.name": "Nom",
  "land.reg.namePh": "Nom...",
  "land.reg.passwordHelp": "Crea una contrasenya segura amb un mínim de 8 dígits, que inclogui com a mínim una majúscula, una minúscula, un número i un símbol.",
  "land.reg.confirm": "Confirma la contrasenya",
  "land.reg.confirmPh": "Confirmar contrasenya...",
  "land.reg.passMismatch": "Les contrasenyes no coincideixen",
  "land.reg.passShort": "La contrasenya ha de tenir almenys 8 caràcters",
  "land.reg.sent": "Sol·licitud preparada. Et contactarem aviat.",
  "land.reg.btn": "Continua amb correu electrònic",
  "land.reg.terms1": "En registrar-te a EasyRural, acceptes els nostres",
  "land.reg.haveAccount": "Ja tens compte?",
  "land.reg.login": "Iniciar sessió",

  "land.pre.title": "Comença el teu període de prova gratuït avui mateix i utilitza el cupó",
  "land.pre.welcome": "WELCOME50",
  "land.pre.titleEnd": "per obtenir un descompte del 50% durant 3 mesos.",
  "land.pre.intro": "Prova EasyRural gratis durant 14 dies i experimenta la comoditat de gestionar el teu allotjament rural.",
  "land.pre.tipo": "Indica si el teu allotjament turístic és de lloguer complet o per habitacions.",
  "land.pre.completo": "Complet",
  "land.pre.habitaciones": "Per habitacions",
  "land.pre.planBasico": "Rural Bàsic",
  "land.pre.planBasicoSub": "Facilita i automatitza els processos essencials per a la gestió del teu allotjament de turisme rural.",
  "land.pre.planRoomsSub": "Gestiona allotjaments per habitacions amb tarifes escalables segons el teu volum.",
  "land.pre.desde": "Des de",
  "land.pre.mes": "€/mes",
  "land.pre.anio": "€/any",
  "land.pre.durante3": "Durant 3 mesos",
  "land.pre.descuento15": "Descompte 15%",
  "land.pre.kitDigital": "* Subvencionable amb el Kit Digital",
  "land.pre.feat1": "Sistema de reserves (PMS)",
  "land.pre.feat2": "Gestió de la relació amb els hostes (CRM)",
  "land.pre.feat3": "Elaboració d'informes policials",
  "land.pre.feat4": "Control de caixa i facturació",
  "land.pre.feat5": "Elaboració d'informes financers",
  "land.pre.feat6": "1 usuari",
  "land.pre.feat7": "4 idiomes disponibles",
  "land.pre.feat8": "Suport els 365 dies de l'any",
  "land.pre.feat9": "10 habitacions incloses",
  "land.pre.feat10": "2€ extra cada 10 habitacions (Màx. 60)",
  "land.pre.empresasTitle": "Ets una empresa que gestiona més de 5 allotjaments turístics?",
  "land.pre.empresasDesc": "A EasyRural oferim tarifes especials i personalitzades que s'adapten al teu volum de negoci. Obtén accés complet a totes les funcionalitats a un preu exclusiu.",
  "land.pre.contactanos": "Contacta'ns",

  "land.eq.label": "Equip",
  "land.eq.h1": "Digitalització, eficiència i simplicitat",
  "land.eq.intro1": "EasyRural arriba per trencar barreres i portar la revolució digital als allotjaments de turisme rural.",
  "land.eq.intro2": "Prou de tecnologies complicades i inaccessibles per als professionals del turisme rural! Naixem amb l'objectiu d'alliberar el potencial de la digitalització.",
  "land.eq.misionTit": "La nostra missió",
  "land.eq.misionDesc": "La nostra missió és modernitzar el món rural a través del nostre ecosistema digital.",
  "land.eq.visionTit": "La nostra visió",
  "land.eq.visionDesc": "No pararem fins ser l'empresa líder en la transformació digital del turisme rural.",
  "land.eq.valoresTit": "Els nostres valors",
  "land.eq.v1.t": "Innovació",
  "land.eq.v1.d": "La nostra cultura empresarial ens situa a l'avantguarda, creant solucions diferents i úniques.",
  "land.eq.v2.t": "Eficiència",
  "land.eq.v2.d": "Tots els nostres processos estan enfocats en l'optimització del temps i els resultats.",
  "land.eq.v3.t": "Passió pel client",
  "land.eq.v3.d": "Cada pas el donem posant les persones al centre de la nostra activitat.",
  "land.eq.v4.t": "Especialització",
  "land.eq.v4.d": "Compromesos a oferir una solució digital adaptada als professionals del turisme rural.",
  "land.eq.v5.t": "Sostenibilitat",
  "land.eq.v5.d": "Accions respectuoses amb el medi ambient i amb la societat que ens envolta.",
  "land.eq.detrasTit": "Qui hi ha darrere d'EasyRural?",
  "land.eq.m1.role": "CEO",
  "land.eq.m1.bio": "Professional del turisme amb més de 7 anys d'experiència en la gestió d'allotjaments turístics. Especialitzat en projectes de turisme rural.",
  "land.eq.m2.role": "CTO",
  "land.eq.m2.bio": "Desenvolupador d'aplicacions informàtiques i director de projectes. Soci fundador d'Avantiam.",

  "land.faq.title": "PREGUNTES FREQÜENTS",
  "land.faq.q1": "EasyRural ofereix alguna prova gratuïta?",
  "land.faq.a1": "Sí, oferim una prova gratuïta de 14 dies sense compromís.",
  "land.faq.q2": "Es pot utilitzar EasyRural des de qualsevol dispositiu?",
  "land.faq.a2": "Sí, plataforma web responsive per a ordinador, tauleta i mòbil.",
  "land.faq.q3": "Necessito experiència prèvia en eines digitals?",
  "land.faq.a3": "No, interfície intuïtiva amb instruccions clares, accessible per a nous en el món digital.",
  "land.faq.q4": "Té EasyRural l'opció de registrar més d'un allotjament?",
  "land.faq.a4": "Sí, connecta diversos allotjaments a un sol usuari i canvia entre ells des del menú superior.",
  "land.faq.q5": "Com puc contactar amb EasyRural?",
  "land.faq.a5": "Per email a info@easyrural.net (24/7) o al +34 649 13 35 41 (de 8h a 14h). També al Discord.",
  "land.faq.q6": "Puc connectar amb portals de reserva vacacional?",
  "land.faq.a6": "Treballem en la connectivitat amb Booking, Airbnb, VRBO i Expedia mitjançant API. iCal per a 100+ portals.",
  "land.faq.q7": "És possible personalitzar les tarifes segons demanda i temporada?",
  "land.faq.a7": "Sí, ajusta tarifes segons demanda/temporada amb preus alts/baixos i descomptes especials.",
  "land.faq.q8": "Puc personalitzar les polítiques de reserva?",
  "land.faq.a8": "Sí, regles de cancel·lació, polítiques de dipòsit i requisits específics.",
  "land.faq.q9": "Genera el sistema informes policials automatitzats?",
  "land.faq.a9": "Sí, crea informes policials automàticament a partir de la informació registrada.",
  "land.faq.q10": "Com es realitza la facturació de les reserves?",
  "land.faq.a10": "Factures digitals ràpidament que envies directament als hostes per correu.",
  "land.faq.q11": "Puc administrar les factures de proveïdors?",
  "land.faq.a11": "Sí, registres i administres factures de proveïdors amb conciliació amb pagaments.",
  "land.faq.q12": "Com m'ajuda amb l'estat de caixa?",
  "land.faq.a12": "Seguiment actualitzat en temps real per metodologia de pagament.",
  "land.faq.q13": "Puc generar informes financers?",
  "land.faq.a13": "Sí, informes detallats d'ingressos i despeses, i resums d'impostos per al teu gestor.",
  "land.faq.q14": "Com millorar l'experiència dels meus hostes?",
  "land.faq.a14": "El CRM centralitza informació. Personalitzes interaccions i augmentes la fidelitat.",
  "land.faq.q15": "Quins beneficis té registrar proveïdors?",
  "land.faq.a15": "Simplifica la introducció de factures i automatitza la recuperació de dades.",
  "land.faq.q16": "És segur emmagatzemar la informació?",
  "land.faq.a16": "Sí, EasyRural compleix amb GDPR i aplica mesures de seguretat per a la informació sensible.",

  "land.ct.formTitle": "Formulari de contacte",
  "land.ct.alsoTitle": "Contacta'ns també",
  "land.ct.fName": "Nom complet *",
  "land.ct.fEmail": "Email *",
  "land.ct.fPhone": "Telèfon",
  "land.ct.fSubject": "Assumpte *",
  "land.ct.fMessage": "Missatge *",
  "land.ct.fAccept": "He llegit i accepto la",
  "land.ct.fPrivacy": "POLÍTICA DE PRIVACITAT",
  "land.ct.fSubmit": "Enviar missatge",
  "land.ct.sent": "Missatge preparat al teu client de correu.",
  "land.ct.byEmail": "Email",
  "land.ct.byPhone": "Telèfon",
  "land.ct.byDiscord": "Discord",
  "land.ct.discordCta": "Uneix-te al servidor",
  "land.ct.discordSub": "Suport per videotrucada",

  "land.bl.title": "Blog EasyRural",
  "land.bl.subtitle": "Consells, novetats i casos d'èxit per a gestors d'allotjaments rurals.",
  "land.bl.read": "Llegir article →",
  "land.bl.p1.t": "5 raons per digitalitzar la teva casa rural el 2026",
  "land.bl.p1.d": "Descobreix per què la digitalització és clau per mantenir-te competitiu.",
  "land.bl.p1.date": "12 gen 2026",
  "land.bl.p2.t": "Registre únic de lloguer turístic: el que has de saber",
  "land.bl.p2.d": "Guia sobre el nou registre obligatori per a lloguers turístics a Espanya.",
  "land.bl.p2.date": "5 gen 2026",
  "land.bl.p3.t": "Sincronització iCal: la clau per evitar overbookings",
  "land.bl.p3.d": "Com mantenir el calendari actualitzat a Booking, Airbnb i 100+ plataformes.",
  "land.bl.p3.date": "20 des 2025",

  "land.fn.queIncluye": "Què inclou?",
  "land.fn.empiezaGratis": "Comença gratis",

  "land.fn.reservas.hero": "Centralitza i automatitza les reserves del teu allotjament rural en una única plataforma.",
  "land.fn.reservas.desc": "Verifica disponibilitat en temps real, crea pressupostos automatitzats i fa seguiment de pagaments des d'una sola plataforma.",
  "land.fn.reservas.f1.t": "Creació i seguiment complet de reserves",
  "land.fn.reservas.f1.d": "Verifica disponibilitat en temps real, crea pressupostos i fes seguiment de pagaments.",
  "land.fn.reservas.f2.t": "Tarifes i calendari sota control",
  "land.fn.reservas.f2.d": "Ajusta tarifes i calendari de forma flexible amb preus segons temporada.",
  "land.fn.reservas.f3.t": "Sincronització automàtica amb 100+ portals",
  "land.fn.reservas.f3.d": "Connecta amb OTAs com Booking o Airbnb. Evita l'overbooking.",
  "land.fn.reservas.f4.t": "Registre digital d'hostes",
  "land.fn.reservas.f4.d": "Compleix amb la normativa amb check-in en línia o pels propis clients.",
  "land.fn.reservas.f5.t": "Facturació integrada",
  "land.fn.reservas.f5.d": "Factures digitals per cada reserva enviades als hostes.",

  "land.fn.facturacion.hero": "Factura electrònica i control financer per al teu allotjament rural sense maldecaps.",
  "land.fn.facturacion.desc": "Emet factures digitals, registra les rebudes i obté informes financers automatitzats per complir la normativa.",
  "land.fn.facturacion.f1.t": "Facturació digital per reserva",
  "land.fn.facturacion.f1.d": "Emet factures digitals en segons i envia-les automàticament als hostes.",
  "land.fn.facturacion.f2.t": "Registre de factures rebudes",
  "land.fn.facturacion.f2.d": "Controla factures de proveïdors amb conciliació amb pagaments.",
  "land.fn.facturacion.f3.t": "Control de caixa en temps real",
  "land.fn.facturacion.f3.d": "Visualitza l'estat de caixa per mètode de pagament.",
  "land.fn.facturacion.f4.t": "Informes financers automàtics",
  "land.fn.facturacion.f4.d": "Informes detallats d'ingressos i despeses. Resums per al teu gestor.",
  "land.fn.facturacion.f5.t": "Compliment Verifactu / Factura-e",
  "land.fn.facturacion.f5.d": "Compleix amb la nova normativa espanyola sense esforç.",

  "land.fn.huespedes.hero": "El teu CRM hoteler adaptat al turisme rural.",
  "land.fn.huespedes.desc": "Centralitza dades d'hostes, automatitza informes policials i fidelitza amb interaccions personalitzades.",
  "land.fn.huespedes.f1.t": "CRM centralitzat",
  "land.fn.huespedes.f1.d": "Tota la informació d'hostes en un únic lloc. Històric d'estades i preferències.",
  "land.fn.huespedes.f2.t": "Check-in en línia",
  "land.fn.huespedes.f2.d": "Permet als hostes fer check-in des del mòbil abans d'arribar.",
  "land.fn.huespedes.f3.t": "Informes policials automatitzats",
  "land.fn.huespedes.f3.d": "Genera i envia parts de viatgers a les autoritats sense papereres.",
  "land.fn.huespedes.f4.t": "Fidelització i comunicació",
  "land.fn.huespedes.f4.d": "Personalitza interaccions i augmenta la satisfacció dels hostes.",
  "land.fn.huespedes.f5.t": "Compliment GDPR",
  "land.fn.huespedes.f5.d": "Emmagatzema dades seguint la normativa europea de protecció.",

  "land.fn.motor.hero": "Rep reserves directes des de la teva web sense comissions.",
  "land.fn.motor.desc": "Un motor de reserves integrat a la teva web que evita comissions de portals.",
  "land.fn.motor.f1.t": "Reserves directes sense comissions",
  "land.fn.motor.f1.d": "Els hostes reserven des de la teva web. Zero comissió d'OTA.",
  "land.fn.motor.f2.t": "Disseny responsive",
  "land.fn.motor.f2.d": "Integració visual adaptada a la teva marca i a tots els dispositius.",
  "land.fn.motor.f3.t": "Disponibilitat i tarifes sincronitzades",
  "land.fn.motor.f3.d": "Llegeix disponibilitat del teu PMS — mai vendràs de més.",
  "land.fn.motor.f4.t": "Pagament en línia segur",
  "land.fn.motor.f4.d": "Accepta targeta i registra bestretes seguint el flux del PMS.",
  "land.fn.motor.f5.t": "Multi-idioma i multi-divisa",
  "land.fn.motor.f5.d": "Arriba a clients internacionals en ES, CA, EN i FR.",

  // ─── PANELL ADMIN / MULTI-USUARI (v9.1) ────────────────
  "nav.adminOverview": "Panell admin",
  "nav.users": "Usuaris",

  "admin.title": "Panell d'administració",
  "admin.subtitle": "Vista ràpida de tots els allotjaments actius a EasyRural.",
  "admin.newClient": "+ Nou allotjament",
  "admin.kpi.active": "Actius",
  "admin.kpi.activeSub": "a {n} municipi{s}",
  "admin.kpi.drafts": "Esborranys",
  "admin.kpi.draftsSub": "Sense pla contractat",
  "admin.kpi.complete": "Pla complet",
  "admin.kpi.completeSub": "APIs + Sensors IoT",
  "admin.kpi.basic": "Pla bàsic",
  "admin.kpi.basicSub": "Només APIs externes",
  "admin.mapTitle": "MAPA GLOBAL",
  "admin.listTitle": "ALLOTJAMENTS",
  "admin.noHousings": "Encara no hi ha allotjaments donats d'alta.",

  "card.today": "Avui",
  "card.month": "Mes",
  "card.costMonth": "Cost mes",
  "card.powerNow": "Potència ara",
  "card.openDash": "Obrir dashboard →",
  "card.completeData": "Completar dades →",
  "card.draftNote": "Registrat a la plataforma però sense pla contractat. Falta CUPS i/o instal·lació de sensors.",
  "card.status.ok": "OK",
  "card.status.draft": "Esborrany",
  "card.status.alarm": "{n} alarma{s}",
  "card.plan.none": "sense pla",

  "map.globalTitle": "Mapa global d'allotjaments",
  "map.title": "Mapa d'allotjaments",
  "map.globalSubtitle": "Tots els allotjaments registrats a EasyRural — actius i en esborrany ({n})",
  "map.mineSubtitle": "Els teus allotjaments al mapa ({n})",
  "map.badge.draft": "⚠ En esborrany · sense pla",
  "map.badge.plan": "Pla {plan}",
  "map.noCoords": "No hi ha allotjaments amb coordenades configurades.",

  "empty.adminTitle": "Encara no hi ha allotjaments",
  "empty.clientTitle": "Hola, {name}",
  "empty.clientTitleFallback": "Hola, benvingut/da",
  "empty.adminBody": "Encara no s'ha donat d'alta cap allotjament a la plataforma. Comença donant d'alta el primer client i els seus dispositius apareixeran aquí automàticament.",
  "empty.clientBody": "El teu allotjament encara no està actiu a la plataforma. Quan l'equip d'EasyRural acabi la configuració dels sensors, veuràs aquí el teu consum, petjada de carboni i alarmes en temps real.",
  "empty.firstClient": "+ Donar d'alta el primer client",
  "empty.goMap": "Veure mapa de serveis",
  "empty.contactHint": "Creus que és un error? Contacta amb",

  "users.title": "Usuaris i accessos",
  "users.subtitle": "Comptes actius a la demo i quin allotjament pot veure cadascun.",
  "users.kpi.total": "Usuaris",
  "users.kpi.totalSub": "a la plataforma",
  "users.kpi.admin": "Administradors",
  "users.kpi.adminSub": "Veuen tots els allotjaments",
  "users.kpi.multi": "Multi-allotjament",
  "users.kpi.multiSub": "Propietaris de ≥2 cases",
  "users.kpi.single": "Individuals",
  "users.kpi.singleSub": "Una única casa",
  "users.col.name": "Nom",
  "users.col.email": "Email",
  "users.col.role": "Rol",
  "users.col.access": "Allotjaments amb accés",
  "users.col.password": "Contrasenya demo",
  "users.role.admin": "Administrador",
  "users.role.cliente": "Client",
  "users.role.adminFull": "Admin global",
  "users.role.multi": "Multi-owner",
  "users.access.all": "Tots (inclou esborranys)",
  "users.demoNoteTitle": "⚠ Només demo",
  "users.demoNote": "aquests comptes estan cablejats al frontend per a la presentació. En producció es substitueixen per autenticació real (JWT + hash de contrasenya al backend).",

  "edit.title": "Editar {name}",
  "edit.subtitle": "Completa o actualitza les dades de l'allotjament.",
  "edit.saveChanges": "Desar canvis",
  "edit.saveDraft": "Desar com a esborrany",

  "newClient.draftToggle": "Desar com a esborrany",
  "newClient.draftHelp": "L'allotjament queda registrat a EasyRural (visible al mapa) però SENSE pla contractat ni CUPS. Es pot completar més endavant.",
  "newClient.saveDraft": "Desar esborrany",
};

const EN = {
  "common.loading": "Loading…",
  "common.error": "Error",
  "common.retry": "Retry",
  "common.noData": "No data",
  "common.connected": "Connected",
  "common.waitingSensors": "Waiting for sensor data…",
  "common.waiting": "Waiting for data…",
  "common.live": "LIVE",
  "common.backHome": "← Home",

  "nav.status": "Sensor status",
  "nav.map": "Properties map",
  "nav.dashboard": "Dashboard",
  "nav.consumption": "Consumption & costs",
  "nav.forecast": "Forecast",
  "nav.contract": "Electricity contract",
  "nav.savings": "Savings",
  "nav.alarms": "Notifications",
  "nav.actuators": "Maintenance",
  "nav.install": "Installation",
  "nav.sec.Gestión": "Management",
  "nav.sec.Alojamiento": "Property",
  "nav.sec.Equipos": "Devices",
  "nav.newClient": "New client",
  "nav.agenda": "Calendar",
  "nav.guests": "Guests",
  "nav.access": "Access",
  "nav.casaRural": "Property",
  "nav.version": "Version 9.0 (live)",

  "login.title": "Admin panel",
  "login.passwordLabel": "Password",
  "login.submit": "Sign in",
  "login.error": "Wrong password",

  "header.logout": "Sign out",
  "header.adminPanel": "Admin panel",

  "footer.copyright": "© 2026 EasyRural · Energy Module · UPC + EasyRural (DIH 2024)",
  "footer.support": "Support",

  "status.title": "Property status",
  "status.subtitle": "Active services in {name}",
  "status.plan": "Plan",
  "status.activeServices": "Active services",
  "status.installedDevices": "Installed devices",
  "status.devicesUnit": "devices",
  "status.alarms7d": "Alarms (7D)",
  "status.alarmsUnit": "events",
  "status.availability30d": "30-day availability",
  "status.notInstalled": "Not installed",
  "status.noRecent": "No recent readings",
  "status.deviceSingle": "device",
  "status.devicePlural": "devices",
  "status.actuator": "actuator",
  "status.actuatorPlural": "actuators",
  "status.sensors.temp": "Temperature sensors",
  "status.sensors.electric": "Electric sensors",
  "status.sensors.water": "Water sensors",
  "status.sensors.consumption": "Electricity consumption",
  "status.sensors.meteo": "Weather",
  "status.sensors.price": "Electricity price",
  "status.sensors.co2": "Carbon footprint",
  "status.sensors.alarms": "Alarms & alerts",
  "status.sensors.control": "Remote control",
  "status.sensors.connection": "Sensor connection",
  "status.calc": "Automatic calc",
  "status.botTelegram": "Telegram Bot",
  "status.valve": "Automatic valve",
  "status.localServer": "Local server",

  "map.title": "Properties map",
  "map.subtitle": "Geographic distribution of managed properties",

  "dashboard.title": "Dashboard",
  "dashboard.subtitle": "Real-time property summary",

  "pvpc.cheapest": "CHEAPEST",
  "pvpc.now": "NOW",
  "pvpc.expensive": "MOST EXPENSIVE",
  "pvpc.cleanest": "CLEANEST",
  "pvpc.dirtiest": "DIRTIEST",

  "consumption.title": "Consumption & costs",
  "consumption.subtitle": "Your electricity use and cost in {name}",
  "consumption.costeMes": "Cost, last 30 days",
  "consumption.consumoMes": "Use, last 30 days",
  "consumption.precioMedio": "Avg price",
  "consumption.efficiency": "Use / m²",
  "consumption.efficiencyNote": "Annual comparison available with full history",
  "consumption.noHistory": "No history to compare",

  "franja.cara": "Peak hour",
  "franja.normal": "Standard hour",
  "franja.barata": "Off-peak hour",

  "conclusions.title": "Auto insights",
  "conclusions.sub": "Interpretive summary of the data",

  "forecast.title": "Consumption forecast",
  "forecast.subtitle": "Forecast based on Datadis history for {name}",

  "contract.title": "Electricity contract",
  "contract.subtitle": "Electricity contract data for {name}",
  "contract.id": "Identifier",
  "contract.name": "Name",
  "contract.cups": "CUPS code",
  "contract.distrib": "Distributor (code)",
  "contract.province": "Province",
  "contract.muniInE": "Municipality (INE)",
  "contract.cp": "Postal code",
  "contract.address": "Address",
  "contract.surface": "Surface (m²)",
  "contract.plan": "Contracted plan",

  "alarms.title": "Notifications and alarms",
  "alarms.subtitle": "Events of the last 60 days — {name}",
  "alarms.none": "No alarms in the last 60 days",
  "alarms.col.date": "Date",
  "alarms.col.severity": "Severity",
  "alarms.col.type": "Type",
  "alarms.col.detail": "Detail",

  "actuators.title": "Maintenance / Actuators",
  "actuators.subtitle": "Remote control of actuators configured in {name}",
  "actuators.none": "This property has no actuators configured.",
  "actuators.activate": "Turn on",
  "actuators.deactivate": "Turn off",

  "newClient.title": "New client",
  "newClient.subtitle": "Create a new property. Fields with * are required.",
  "newClient.saving": "Saving...",
  "newClient.create": "Create client",

  // ─── LANDING EN ─────────────────────────────────────────
  "land.nav.funcionalidades": "Features",
  "land.nav.precio": "Pricing",
  "land.nav.equipo": "Team",
  "land.nav.faqs": "FAQs",
  "land.nav.contacto": "Contact",
  "land.nav.blog": "Blog",
  "land.nav.acceder": "Sign in",
  "land.nav.pruebaGratuita": "Free trial",
  "land.nav.gestionReservas": "Booking management",
  "land.nav.gestionFacturacion": "Billing management",
  "land.nav.gestionHuespedes": "Guest management",
  "land.nav.motorReservas": "Booking engine",

  "land.foot.derechos": "All rights reserved.",
  "land.foot.soporteTecnico": "Technical support",
  "land.foot.soporteOnline": "24/7 Online support",
  "land.foot.de8a14": "8h to 14h",
  "land.foot.infoLegal": "Legal information",
  "land.foot.politicaPriv": "Privacy policy",
  "land.foot.politicaCookies": "Cookie policy",
  "land.foot.terminos": "Terms and conditions",
  "land.foot.avisoLegal": "Legal notice",
  "land.foot.masInfo": "More information",
  "land.foot.acerca": "About EasyRural",
  "land.foot.moduloEnergia": "Energy Module",
  "land.foot.upcDih": "UPC + EasyRural (DIH 2024)",
  "land.foot.replica": "© 2026 EasyRural · Visual replica for Energy Module demonstration purposes",

  "land.hero.si": "yes!",
  "land.hero.rural": "Rural,",
  "land.hero.digital": "Digital,",
  "land.hero.tambien": "too!",
  "land.hero.pms": "The ultimate PMS to save time managing your rural property.",
  "land.hero.intro": "With EasyRural automate bookings, manage your property from anywhere, and forget about paperwork. All in a single, easy and tailored program.",
  "land.hero.empiezaGratis": "Start free",

  "land.home.gestionaTitle": "Manage your property easily and efficiently with our software for rural houses.",
  "land.home.gestionaSub": "With EasyRural, access management systems specialized in rural tourism that let you save up to 80% of the time you used to spend on admin tasks.",
  "land.home.saberMas": "Learn more",
  "land.home.fn1.t": "Booking management",
  "land.home.fn1.d": "Manage bookings and availability of your property and automate distribution thanks to connectivity with main booking portals.",
  "land.home.fn2.t": "Billing management",
  "land.home.fn2.d": "Issue the invoices for booking charges and record received invoices. Maintain detailed control over the financial situation of your rural property.",
  "land.home.fn3.t": "Client management",
  "land.home.fn3.d": "Centralize and manage client information to optimize interactions, increase loyalty and automate sending police reports.",

  "land.home.adaptadoTitle": "Adapted to all kinds of rural properties",
  "land.home.adaptadoSub": "Whether you manage an entire property or by rooms, our program adapts to you. Easyrural is versatile, intuitive and scalable software.",
  "land.home.alqCompleto.t": "Whole property rental",
  "land.home.alqCompleto.d": "Manage bookings, availability and rates for entire property rental visually and easily, thanks to a control panel in monthly calendar format.",
  "land.home.alqHab.t": "Room-based rental",
  "land.home.alqHab.d": "Register types and number of rooms in your property and manage availability, rates and bookings with a very intuitive control panel.",

  "land.home.porqueTitle": "Why is EasyRural the best management software?",
  "land.home.porqueSub": "We have many reasons, but here are 6 key ones:",
  "land.home.r1.t": "Specialization",
  "land.home.r1.d": "Use a digital platform designed specifically for rural tourism properties, offering personalized, high-quality service.",
  "land.home.r2.t": "Simplicity",
  "land.home.r2.d": "Explore an easy-to-use and understand platform, regardless of your experience with rural property management and digital tools.",
  "land.home.r3.t": "Efficiency",
  "land.home.r3.d": "Save up to 80% of the time you used to invest in managing your property, so you can dedicate more time to yourself and your guests.",
  "land.home.r4.t": "Legal compliance",
  "land.home.r4.d": "Comply with the new Spanish regulations on electronic invoicing and guest registration, ensuring full transparency and legal compliance.",
  "land.home.r5.t": "Cloud management",
  "land.home.r5.d": "Manage your property from any device and any time, with no need to install our program.",
  "land.home.r6.t": "Connectivity",
  "land.home.r6.d": "Automate property marketing by connecting EasyRural with your listings on vacation rental platforms like Booking, Airbnb and VRBO.",

  "land.home.f1.t": "Complete booking creation and tracking",
  "land.home.f1.d": "Verify availability in real time, create automated quotes, confirm or cancel bookings, and track payments and updates from a single platform.",
  "land.home.f2.t": "Rates and calendar under control",
  "land.home.f2.d": "Adjust rates and availability calendar flexibly. Configure prices by season, define specific booking policies and update changes in real time.",
  "land.home.f3.t": "Automatic sync with 100+ portals",
  "land.home.f3.d": "Connect your property with major OTAs like Booking or Airbnb and automate booking, availability and rate sync. Avoid overbooking.",
  "land.home.f4.t": "Digital guest registration with automated reports",
  "land.home.f4.d": "Comply with current regulations thanks to digital guest registration. Do online check-in or let your clients do it themselves.",
  "land.home.f5.t": "Integrated and hassle-free billing",
  "land.home.f5.d": "Issue digital invoices for each booking and send them directly to your guests. Record all charges easily.",

  "land.home.visibilidadTitle": "Increase your visibility and avoid booking errors",
  "land.home.visibilidad1": "With EasyRural, forget about overbookings and manually updating each calendar. Our solution for rural houses connects with 100+ booking portals via iCal integration.",
  "land.home.visibilidad2": "Every time a new booking is registered, the system updates all channels where you're present, keeping information coherent and avoiding errors.",
  "land.home.portalesConect": "connected portals",
  "land.home.entreOtros": "... and many more portals!",

  "land.banner.title": "Try EasyRural completely free and unlimited for 14 days.",
  "land.banner.desc": "In just 10 minutes, EasyRural will be ready and running at your tourism property, with no complicated installations.",

  "land.home.confianTitle": "Rural properties that trust our PMS:",
  "land.home.opinanTitle": "What do clients say about our systems?",
  "land.home.test1.t": "Easy to use and very efficient",
  "land.home.test1.q": "EasyRural has simplified the management of our mountain shelter. Its intuitive interface makes booking, billing and guest registration management easy.",
  "land.home.test2.t": "Time and effort savings",
  "land.home.test2.q": "Thanks to EasyRural, I can now dedicate more time to attending my guests and improving their experience instead of losing it on admin tasks.",
  "land.home.test3.t": "Simplicity for beginners",
  "land.home.test3.q": "As a rural property owner with no prior experience with digital tools, EasyRural has been a great discovery. The platform is incredibly easy to use.",
  "land.home.test4.t": "Exceptional technical support!",
  "land.home.test4.q": "What impressed me most about EasyRural was the excellent customer service. Every time I had a question or issue, the support team responded quickly and effectively.",

  "land.login.title": "Sign in",
  "land.login.email": "Email address",
  "land.login.emailPh": "Email address...",
  "land.login.password": "Password",
  "land.login.passwordPh": "Password...",
  "land.login.err": "Wrong email or password",
  "land.login.btn": "Continue with email",
  "land.login.terms1": "By signing in to EasyRural, you accept our",
  "land.login.terms2": "and our",
  "land.login.privacy": "privacy policy",
  "land.login.noAccount": "Don't have an account?",
  "land.login.createAccount": "Create account",

  "land.reg.title": "Create user account",
  "land.reg.name": "Name",
  "land.reg.namePh": "Name...",
  "land.reg.passwordHelp": "Create a secure password with at least 8 characters, including at least one uppercase letter, one lowercase, one number and one symbol.",
  "land.reg.confirm": "Confirm password",
  "land.reg.confirmPh": "Confirm password...",
  "land.reg.passMismatch": "Passwords don't match",
  "land.reg.passShort": "Password must be at least 8 characters",
  "land.reg.sent": "Request prepared. We'll contact you soon.",
  "land.reg.btn": "Continue with email",
  "land.reg.terms1": "By registering at EasyRural, you accept our",
  "land.reg.haveAccount": "Already have an account?",
  "land.reg.login": "Sign in",

  "land.pre.title": "Start your free trial today and use the coupon",
  "land.pre.welcome": "WELCOME50",
  "land.pre.titleEnd": "to get a 50% discount for 3 months.",
  "land.pre.intro": "Try EasyRural free for 14 days and experience the convenience of efficiently managing your rural property.",
  "land.pre.tipo": "Indicate whether your tourism property is whole rental or by rooms.",
  "land.pre.completo": "Whole",
  "land.pre.habitaciones": "By rooms",
  "land.pre.planBasico": "Rural Basic",
  "land.pre.planBasicoSub": "Facilitates and automates the essential processes for managing your rural tourism property.",
  "land.pre.planRoomsSub": "Manage room-based properties with scalable rates based on your volume.",
  "land.pre.desde": "From",
  "land.pre.mes": "€/month",
  "land.pre.anio": "€/year",
  "land.pre.durante3": "For 3 months",
  "land.pre.descuento15": "15% discount",
  "land.pre.kitDigital": "* Subsidized with the Digital Kit",
  "land.pre.feat1": "Booking system (PMS)",
  "land.pre.feat2": "Guest relationship management (CRM)",
  "land.pre.feat3": "Police report generation",
  "land.pre.feat4": "Cash flow and billing control",
  "land.pre.feat5": "Financial reports generation",
  "land.pre.feat6": "1 user",
  "land.pre.feat7": "4 languages available",
  "land.pre.feat8": "Support 365 days a year",
  "land.pre.feat9": "10 rooms included",
  "land.pre.feat10": "€2 extra each 10 rooms (Max. 60)",
  "land.pre.empresasTitle": "Are you a company managing more than 5 tourism properties?",
  "land.pre.empresasDesc": "At EasyRural we offer special and customized rates that adapt to your business volume. Get full access to all our features at an exclusive price.",
  "land.pre.contactanos": "Contact us",

  "land.eq.label": "Team",
  "land.eq.h1": "Digitization, efficiency and simplicity",
  "land.eq.intro1": "EasyRural arrives to break barriers and bring the digital revolution to rural tourism properties.",
  "land.eq.intro2": "Enough of complicated and inaccessible technologies for rural tourism professionals! We're born to free the digital potential of rural properties.",
  "land.eq.misionTit": "Our mission",
  "land.eq.misionDesc": "Our mission is to modernize the rural world through our digital ecosystem.",
  "land.eq.visionTit": "Our vision",
  "land.eq.visionDesc": "We won't stop until we're the leading company in rural tourism digital transformation.",
  "land.eq.valoresTit": "Our values",
  "land.eq.v1.t": "Innovation",
  "land.eq.v1.d": "Our company culture places us at the forefront, creating different and unique solutions.",
  "land.eq.v2.t": "Efficiency",
  "land.eq.v2.d": "All our processes are focused on optimizing time and results.",
  "land.eq.v3.t": "Customer passion",
  "land.eq.v3.d": "We take each step putting people at the center of our activity.",
  "land.eq.v4.t": "Specialization",
  "land.eq.v4.d": "Committed to offering a digital solution adapted to rural tourism professionals.",
  "land.eq.v5.t": "Sustainability",
  "land.eq.v5.d": "All actions related to our activity are respectful with environment and society.",
  "land.eq.detrasTit": "Who's behind EasyRural?",
  "land.eq.m1.role": "CEO",
  "land.eq.m1.bio": "Tourism professional with over 7 years of experience managing tourism properties. Specialized in rural tourism projects.",
  "land.eq.m2.role": "CTO",
  "land.eq.m2.bio": "Software developer and project director in many business applications like Clockio. Co-founder of Avantiam.",

  "land.faq.title": "FREQUENTLY ASKED QUESTIONS",
  "land.faq.q1": "Does EasyRural offer a free trial?",
  "land.faq.a1": "Yes, we offer a 14-day free trial with no commitment.",
  "land.faq.q2": "Can I use EasyRural from any device?",
  "land.faq.a2": "Yes, responsive web platform for desktop, tablet and mobile.",
  "land.faq.q3": "Do I need prior experience with digital tools?",
  "land.faq.a3": "No, intuitive interface with clear instructions, accessible even for newcomers.",
  "land.faq.q4": "Can I register more than one property?",
  "land.faq.a4": "Yes, connect multiple properties to a single user and switch between them.",
  "land.faq.q5": "How can I contact EasyRural?",
  "land.faq.a5": "By email at info@easyrural.net (24/7) or +34 649 13 35 41 (Mon-Fri 8h-14h). Also our Discord.",
  "land.faq.q6": "Can I connect with vacation rental portals?",
  "land.faq.a6": "We're working on connectivity with Booking, Airbnb, VRBO and Expedia via API. iCal for 100+ portals.",
  "land.faq.q7": "Can I customize rates by demand and season?",
  "land.faq.a7": "Yes, adjust rates by demand and season with peak/off-peak pricing and special discounts.",
  "land.faq.q8": "Can I customize booking policies?",
  "land.faq.a8": "Yes, cancellation rules, deposit policies and specific requirements.",
  "land.faq.q9": "Does the system generate automated police reports?",
  "land.faq.a9": "Yes, creates police reports automatically from registered guest information.",
  "land.faq.q10": "How is booking billing done?",
  "land.faq.a10": "Quick digital invoices sent directly to guests by email.",
  "land.faq.q11": "Can I manage supplier invoices?",
  "land.faq.a11": "Yes, register and manage supplier invoices with payment reconciliation.",
  "land.faq.q12": "How does it help with cash flow?",
  "land.faq.a12": "Real-time updated tracking by payment method.",
  "land.faq.q13": "Can I generate financial reports?",
  "land.faq.a13": "Yes, detailed reports of income and expenses, and tax summaries for your accountant.",
  "land.faq.q14": "How can I improve my guests' experience?",
  "land.faq.a14": "CRM centralizes information. Personalize interactions and increase loyalty.",
  "land.faq.q15": "What are the benefits of registering suppliers?",
  "land.faq.a15": "Simplifies invoice entry and automates data retrieval.",
  "land.faq.q16": "Is it safe to store my guests' information?",
  "land.faq.a16": "Yes, EasyRural complies with GDPR and applies security measures for sensitive information.",

  "land.ct.formTitle": "Contact form",
  "land.ct.alsoTitle": "Reach us also",
  "land.ct.fName": "Full name *",
  "land.ct.fEmail": "Email *",
  "land.ct.fPhone": "Phone",
  "land.ct.fSubject": "Subject *",
  "land.ct.fMessage": "Message *",
  "land.ct.fAccept": "I've read and accept the",
  "land.ct.fPrivacy": "PRIVACY POLICY",
  "land.ct.fSubmit": "Send message",
  "land.ct.sent": "Message ready in your mail client.",
  "land.ct.byEmail": "Email",
  "land.ct.byPhone": "Phone",
  "land.ct.byDiscord": "Discord",
  "land.ct.discordCta": "Join the server",
  "land.ct.discordSub": "Video call support",

  "land.bl.title": "EasyRural Blog",
  "land.bl.subtitle": "Tips, news and success stories for rural property managers.",
  "land.bl.read": "Read article →",
  "land.bl.p1.t": "5 reasons to digitize your rural house in 2026",
  "land.bl.p1.d": "Discover why digitization is key to staying competitive.",
  "land.bl.p1.date": "Jan 12, 2026",
  "land.bl.p2.t": "Single tourism rental registry: what you should know",
  "land.bl.p2.d": "Guide on the new mandatory registry for tourism rentals in Spain.",
  "land.bl.p2.date": "Jan 5, 2026",
  "land.bl.p3.t": "iCal sync: the key to avoiding overbookings",
  "land.bl.p3.d": "How to keep your calendar updated on Booking, Airbnb and 100+ platforms.",
  "land.bl.p3.date": "Dec 20, 2025",

  "land.fn.queIncluye": "What's included?",
  "land.fn.empiezaGratis": "Start free",

  "land.fn.reservas.hero": "Centralize and automate your rural property bookings in a single platform.",
  "land.fn.reservas.desc": "Verify availability in real time, create automated quotes, confirm or cancel bookings, and track payments from one platform.",
  "land.fn.reservas.f1.t": "Complete booking creation and tracking",
  "land.fn.reservas.f1.d": "Verify availability in real time, create quotes and track payments.",
  "land.fn.reservas.f2.t": "Rates and calendar under control",
  "land.fn.reservas.f2.d": "Adjust rates and calendar flexibly with seasonal pricing.",
  "land.fn.reservas.f3.t": "Automatic sync with 100+ portals",
  "land.fn.reservas.f3.d": "Connect with OTAs like Booking or Airbnb. Avoid overbooking.",
  "land.fn.reservas.f4.t": "Digital guest registration",
  "land.fn.reservas.f4.d": "Comply with regulations through online or self check-in.",
  "land.fn.reservas.f5.t": "Integrated billing",
  "land.fn.reservas.f5.d": "Digital invoices per booking sent to guests.",

  "land.fn.facturacion.hero": "Electronic invoicing and financial control for your rural property without headaches.",
  "land.fn.facturacion.desc": "Issue digital invoices, register received ones and get automated financial reports.",
  "land.fn.facturacion.f1.t": "Digital invoicing per booking",
  "land.fn.facturacion.f1.d": "Issue digital invoices in seconds and send them automatically.",
  "land.fn.facturacion.f2.t": "Supplier invoice registry",
  "land.fn.facturacion.f2.d": "Control supplier invoices with payment reconciliation.",
  "land.fn.facturacion.f3.t": "Real-time cash flow control",
  "land.fn.facturacion.f3.d": "View cash flow status by payment method.",
  "land.fn.facturacion.f4.t": "Automatic financial reports",
  "land.fn.facturacion.f4.d": "Detailed reports of income and expenses for your accountant.",
  "land.fn.facturacion.f5.t": "Verifactu / Factura-e compliance",
  "land.fn.facturacion.f5.d": "Comply with Spanish electronic invoicing regulation effortlessly.",

  "land.fn.huespedes.hero": "Your hotel CRM adapted to rural tourism.",
  "land.fn.huespedes.desc": "Centralize guest data, automate police reports and build loyalty.",
  "land.fn.huespedes.f1.t": "Centralized CRM",
  "land.fn.huespedes.f1.d": "All guest information in one place. Stay history and preferences.",
  "land.fn.huespedes.f2.t": "Online check-in",
  "land.fn.huespedes.f2.d": "Let guests check in from their phone before arrival.",
  "land.fn.huespedes.f3.t": "Automated police reports",
  "land.fn.huespedes.f3.d": "Generate and send guest reports to authorities without paperwork.",
  "land.fn.huespedes.f4.t": "Loyalty and communication",
  "land.fn.huespedes.f4.d": "Personalize interactions and increase guest satisfaction.",
  "land.fn.huespedes.f5.t": "GDPR compliance",
  "land.fn.huespedes.f5.d": "Store data following European data protection regulation.",

  "land.fn.motor.hero": "Receive direct bookings from your website without commissions.",
  "land.fn.motor.desc": "A booking engine integrated in your website that avoids portal commissions.",
  "land.fn.motor.f1.t": "Direct bookings without commissions",
  "land.fn.motor.f1.d": "Guests book directly from your website. Zero OTA commission.",
  "land.fn.motor.f2.t": "Responsive design",
  "land.fn.motor.f2.d": "Visual integration adapted to your brand and all devices.",
  "land.fn.motor.f3.t": "Synced availability and rates",
  "land.fn.motor.f3.d": "Reads availability from your PMS — you'll never oversell.",
  "land.fn.motor.f4.t": "Secure online payment",
  "land.fn.motor.f4.d": "Accept card and register deposits following the PMS flow.",
  "land.fn.motor.f5.t": "Multi-language and multi-currency",
  "land.fn.motor.f5.d": "Reach international clients in ES, CA, EN and FR.",

  // ─── ADMIN PANEL / MULTI-USER (v9.1) ───────────────────
  "nav.adminOverview": "Admin panel",
  "nav.users": "Users",

  "admin.title": "Administration panel",
  "admin.subtitle": "Quick view of every active housing in EasyRural.",
  "admin.newClient": "+ New housing",
  "admin.kpi.active": "Active",
  "admin.kpi.activeSub": "in {n} municipalit{s|ies|y}",
  "admin.kpi.drafts": "Drafts",
  "admin.kpi.draftsSub": "No plan yet",
  "admin.kpi.complete": "Full plan",
  "admin.kpi.completeSub": "APIs + IoT Sensors",
  "admin.kpi.basic": "Basic plan",
  "admin.kpi.basicSub": "External APIs only",
  "admin.mapTitle": "GLOBAL MAP",
  "admin.listTitle": "HOUSINGS",
  "admin.noHousings": "No housings registered yet.",

  "card.today": "Today",
  "card.month": "Month",
  "card.costMonth": "Month cost",
  "card.powerNow": "Power now",
  "card.openDash": "Open dashboard →",
  "card.completeData": "Complete data →",
  "card.draftNote": "Registered on the platform but with no contracted plan. Missing CUPS and/or sensor install.",
  "card.status.ok": "OK",
  "card.status.draft": "Draft",
  "card.status.alarm": "{n} alarm{s}",
  "card.plan.none": "no plan",

  "map.globalTitle": "Global housings map",
  "map.title": "Housings map",
  "map.globalSubtitle": "All housings registered in EasyRural — active and draft ({n})",
  "map.mineSubtitle": "Your housings on the map ({n})",
  "map.badge.draft": "⚠ Draft · no plan",
  "map.badge.plan": "{plan} plan",
  "map.noCoords": "No housings with coordinates configured.",

  "empty.adminTitle": "No housings yet",
  "empty.clientTitle": "Hi, {name}",
  "empty.clientTitleFallback": "Hi, welcome",
  "empty.adminBody": "No housing has been registered on the platform yet. Start by onboarding your first client — their devices will show up here automatically.",
  "empty.clientBody": "Your housing is not active on the platform yet. As soon as the EasyRural team finishes the sensor setup, you'll see your consumption, carbon footprint and alarms live here.",
  "empty.firstClient": "+ Onboard first client",
  "empty.goMap": "See service map",
  "empty.contactHint": "Think it's a mistake? Contact",

  "users.title": "Users & access",
  "users.subtitle": "Active demo accounts and which housing each one can see.",
  "users.kpi.total": "Users",
  "users.kpi.totalSub": "on the platform",
  "users.kpi.admin": "Administrators",
  "users.kpi.adminSub": "See every housing",
  "users.kpi.multi": "Multi-housing",
  "users.kpi.multiSub": "Owners of ≥2 houses",
  "users.kpi.single": "Single",
  "users.kpi.singleSub": "One single house",
  "users.col.name": "Name",
  "users.col.email": "Email",
  "users.col.role": "Role",
  "users.col.access": "Housings they can access",
  "users.col.password": "Demo password",
  "users.role.admin": "Administrator",
  "users.role.cliente": "Client",
  "users.role.adminFull": "Global admin",
  "users.role.multi": "Multi-owner",
  "users.access.all": "All (drafts included)",
  "users.demoNoteTitle": "⚠ Demo only",
  "users.demoNote": "these accounts are hard-coded in the frontend for the presentation. In production they will be replaced by real authentication (JWT + password hash on backend).",

  "edit.title": "Edit {name}",
  "edit.subtitle": "Complete or update the housing data.",
  "edit.saveChanges": "Save changes",
  "edit.saveDraft": "Save as draft",

  "newClient.draftToggle": "Save as draft",
  "newClient.draftHelp": "The housing is registered in EasyRural (visible on the map) but with NO plan and no CUPS. It can be completed later.",
  "newClient.saveDraft": "Save draft",
};

const FR = {
  "common.loading": "Chargement…",
  "common.error": "Erreur",
  "common.retry": "Réessayer",
  "common.noData": "Aucune donnée",
  "common.connected": "Connecté",
  "common.waitingSensors": "En attente des capteurs…",
  "common.waiting": "En attente des données…",
  "common.live": "EN DIRECT",
  "common.backHome": "← Accueil",

  "nav.status": "État capteurs",
  "nav.map": "Carte des hébergements",
  "nav.dashboard": "Tableau de bord",
  "nav.consumption": "Consommation et coûts",
  "nav.forecast": "Prévision",
  "nav.contract": "Contrat électrique",
  "nav.savings": "Économies",
  "nav.alarms": "Notifications",
  "nav.actuators": "Maintenance",
  "nav.install": "Installation",
  "nav.sec.Gestión": "Gestion",
  "nav.sec.Alojamiento": "Hébergement",
  "nav.sec.Equipos": "Équipements",
  "nav.newClient": "Nouveau client",
  "nav.agenda": "Agenda",
  "nav.guests": "Invités",
  "nav.access": "Accès",
  "nav.casaRural": "Hébergement",
  "nav.version": "Version 9.0 (live)",

  "login.title": "Panneau d'administration",
  "login.passwordLabel": "Mot de passe",
  "login.submit": "Entrer",
  "login.error": "Mot de passe incorrect",

  "header.logout": "Déconnexion",
  "header.adminPanel": "Panneau admin",

  "footer.copyright": "© 2026 EasyRural · Module Énergie · UPC + EasyRural (DIH 2024)",
  "footer.support": "Support",

  "status.title": "État de l'hébergement",
  "status.subtitle": "Services actifs à {name}",
  "status.plan": "Plan",
  "status.activeServices": "Services actifs",
  "status.installedDevices": "Appareils installés",
  "status.devicesUnit": "appareils",
  "status.alarms7d": "Alarmes (7J)",
  "status.alarmsUnit": "événements",
  "status.availability30d": "Disponibilité 30 jours",
  "status.notInstalled": "Non installé",
  "status.noRecent": "Aucune lecture récente",
  "status.deviceSingle": "appareil",
  "status.devicePlural": "appareils",
  "status.actuator": "actionneur",
  "status.actuatorPlural": "actionneurs",
  "status.sensors.temp": "Capteurs de température",
  "status.sensors.electric": "Capteurs électriques",
  "status.sensors.water": "Capteurs d'eau",
  "status.sensors.consumption": "Consommation électrique",
  "status.sensors.meteo": "Météo",
  "status.sensors.price": "Prix de l'électricité",
  "status.sensors.co2": "Empreinte carbone",
  "status.sensors.alarms": "Alarmes et alertes",
  "status.sensors.control": "Contrôle à distance",
  "status.sensors.connection": "Connexion capteurs",
  "status.calc": "Calcul automatique",
  "status.botTelegram": "Bot Telegram",
  "status.valve": "Vanne automatique",
  "status.localServer": "Serveur local",

  "map.title": "Carte des hébergements",
  "map.subtitle": "Distribution géographique des hébergements gérés",

  "dashboard.title": "Tableau de bord",
  "dashboard.subtitle": "Résumé en temps réel de l'hébergement",

  "pvpc.cheapest": "MOINS CHÈRE",
  "pvpc.now": "MAINTENANT",
  "pvpc.expensive": "PLUS CHÈRE",
  "pvpc.cleanest": "PLUS PROPRE",
  "pvpc.dirtiest": "PLUS SALE",

  "consumption.title": "Consommation et coûts",
  "consumption.subtitle": "Votre consommation électrique et son coût à {name}",
  "consumption.costeMes": "Coût, 30 derniers jours",
  "consumption.consumoMes": "Conso, 30 derniers jours",
  "consumption.precioMedio": "Prix moyen",
  "consumption.efficiency": "Conso. / m²",
  "consumption.efficiencyNote": "Comparaison annuelle disponible avec historique complet",
  "consumption.noHistory": "Pas d'historique pour comparer",

  "franja.cara": "Heure pleine",
  "franja.normal": "Heure normale",
  "franja.barata": "Heure creuse",

  "conclusions.title": "Analyses automatiques",
  "conclusions.sub": "Résumé interprétatif des données",

  "forecast.title": "Prévision de consommation",
  "forecast.subtitle": "Prévision basée sur l'historique Datadis pour {name}",

  "contract.title": "Contrat électrique",
  "contract.subtitle": "Données du contrat électrique de {name}",
  "contract.id": "Identifiant",
  "contract.name": "Nom",
  "contract.cups": "Code CUPS",
  "contract.distrib": "Distributeur (code)",
  "contract.province": "Province",
  "contract.muniInE": "Municipalité (INE)",
  "contract.cp": "Code postal",
  "contract.address": "Adresse",
  "contract.surface": "Surface (m²)",
  "contract.plan": "Plan contracté",

  "alarms.title": "Notifications et alarmes",
  "alarms.subtitle": "Événements des 60 derniers jours — {name}",
  "alarms.none": "Aucune alarme dans les 60 derniers jours",
  "alarms.col.date": "Date",
  "alarms.col.severity": "Sévérité",
  "alarms.col.type": "Type",
  "alarms.col.detail": "Détail",

  "actuators.title": "Maintenance / Actionneurs",
  "actuators.subtitle": "Contrôle à distance des actionneurs configurés à {name}",
  "actuators.none": "Cet hébergement n'a pas d'actionneurs configurés.",
  "actuators.activate": "Activer",
  "actuators.deactivate": "Désactiver",

  "newClient.title": "Nouveau client",
  "newClient.subtitle": "Créez un nouvel hébergement. Les champs marqués * sont obligatoires.",
  "newClient.saving": "Enregistrement...",
  "newClient.create": "Créer le client",

  // ─── LANDING FR ─────────────────────────────────────────
  "land.nav.funcionalidades": "Fonctionnalités",
  "land.nav.precio": "Prix",
  "land.nav.equipo": "Équipe",
  "land.nav.faqs": "FAQs",
  "land.nav.contacto": "Contact",
  "land.nav.blog": "Blog",
  "land.nav.acceder": "Se connecter",
  "land.nav.pruebaGratuita": "Essai gratuit",
  "land.nav.gestionReservas": "Gestion des réservations",
  "land.nav.gestionFacturacion": "Gestion de la facturation",
  "land.nav.gestionHuespedes": "Gestion des invités",
  "land.nav.motorReservas": "Moteur de réservation",

  "land.foot.derechos": "Tous droits réservés.",
  "land.foot.soporteTecnico": "Support technique",
  "land.foot.soporteOnline": "24/7 Support en ligne",
  "land.foot.de8a14": "De 8h à 14h",
  "land.foot.infoLegal": "Information légale",
  "land.foot.politicaPriv": "Politique de confidentialité",
  "land.foot.politicaCookies": "Politique de cookies",
  "land.foot.terminos": "Conditions générales",
  "land.foot.avisoLegal": "Mentions légales",
  "land.foot.masInfo": "Plus d'information",
  "land.foot.acerca": "À propos d'EasyRural",
  "land.foot.moduloEnergia": "Module Énergie",
  "land.foot.upcDih": "UPC + EasyRural (DIH 2024)",
  "land.foot.replica": "© 2026 EasyRural · Réplique visuelle à des fins de démonstration du Module Énergie",

  "land.hero.si": "oui !",
  "land.hero.rural": "Rural,",
  "land.hero.digital": "Numérique,",
  "land.hero.tambien": "aussi !",
  "land.hero.pms": "Le PMS définitif pour gagner du temps dans la gestion de votre hébergement rural.",
  "land.hero.intro": "Avec EasyRural automatisez les réservations, gérez votre hébergement depuis n'importe où et oubliez la paperasse. Tout dans un seul programme, facile et sur mesure.",
  "land.hero.empiezaGratis": "Commencer gratuitement",

  "land.home.gestionaTitle": "Gérez votre hébergement facilement et efficacement avec notre logiciel pour gîtes ruraux.",
  "land.home.gestionaSub": "Avec EasyRural, accédez à des systèmes de gestion spécialisés en tourisme rural qui vous permettent d'économiser jusqu'à 80% du temps consacré à l'administration de votre activité.",
  "land.home.saberMas": "En savoir plus",
  "land.home.fn1.t": "Gestion des réservations",
  "land.home.fn1.d": "Gérez les réservations et la disponibilité de votre hébergement et automatisez sa distribution grâce à la connectivité avec les principaux portails.",
  "land.home.fn2.t": "Gestion de la facturation",
  "land.home.fn2.d": "Émettez les factures correspondant aux charges des réservations et enregistrez les factures reçues. Tenez un contrôle minutieux de la situation économique de votre gîte rural.",
  "land.home.fn3.t": "Gestion des clients",
  "land.home.fn3.d": "Centralisez et gérez les informations de vos clients pour optimiser l'interaction, augmenter la fidélisation et automatiser l'envoi de rapports policiers.",

  "land.home.adaptadoTitle": "Adapté à tout type d'hébergement rural",
  "land.home.adaptadoSub": "Que vous gériez un hébergement entier ou par chambres, notre programme s'adapte à vous. Easyrural est un logiciel polyvalent, intuitif et évolutif.",
  "land.home.alqCompleto.t": "Location complète",
  "land.home.alqCompleto.d": "Gérez réservations, disponibilités et tarifs de votre hébergement touristique en location complète de façon visuelle et simple, grâce à un panneau de contrôle au format calendrier mensuel.",
  "land.home.alqHab.t": "Location par chambres",
  "land.home.alqHab.d": "Enregistrez les typologies et le nombre de chambres de votre hébergement touristique et gérez disponibilités, tarifs et réservations grâce à un panneau de contrôle très intuitif.",

  "land.home.porqueTitle": "Pourquoi EasyRural est-il le meilleur logiciel de gestion ?",
  "land.home.porqueSub": "Nous avons de nombreuses raisons, mais voici 6 motifs clés :",
  "land.home.r1.t": "Spécialisation",
  "land.home.r1.d": "Utilisez une plateforme numérique conçue spécifiquement pour les hébergements de tourisme rural, offrant un service personnalisé et de haute qualité.",
  "land.home.r2.t": "Simplicité",
  "land.home.r2.d": "Explorez une plateforme facile à utiliser et à comprendre, peu importe votre niveau d'expérience en gestion d'hébergements ruraux et outils numériques.",
  "land.home.r3.t": "Efficacité",
  "land.home.r3.d": "Économisez jusqu'à 80% du temps que vous investissiez dans la gestion de votre hébergement, ainsi vous pourrez consacrer plus de temps à vous-même et à vos invités.",
  "land.home.r4.t": "Conformité légale",
  "land.home.r4.d": "Conformez-vous aux nouvelles réglementations espagnoles de facturation électronique et d'enregistrement des invités.",
  "land.home.r5.t": "Gestion dans le cloud",
  "land.home.r5.d": "Administrez votre hébergement depuis n'importe quel appareil et à tout moment, sans besoin d'installer notre programme.",
  "land.home.r6.t": "Connectivité",
  "land.home.r6.d": "Automatisez la commercialisation de votre hébergement en connectant EasyRural avec vos annonces sur Booking, Airbnb et VRBO.",

  "land.home.f1.t": "Création et suivi complet des réservations",
  "land.home.f1.d": "Vérifiez la disponibilité en temps réel, créez des devis automatisés, confirmez ou annulez des réservations et suivez paiements et mises à jour depuis une seule plateforme.",
  "land.home.f2.t": "Tarifs et calendrier sous contrôle",
  "land.home.f2.d": "Ajustez vos tarifs et calendrier de disponibilité de façon flexible. Configurez les prix par saison, définissez des politiques de réservation spécifiques et mettez à jour en temps réel.",
  "land.home.f3.t": "Synchronisation automatique avec 100+ portails",
  "land.home.f3.d": "Connectez votre hébergement avec les principales OTAs comme Booking ou Airbnb et automatisez la synchronisation de réservations, disponibilités et tarifs. Évitez l'overbooking.",
  "land.home.f4.t": "Enregistrement numérique des invités avec rapports automatisés",
  "land.home.f4.d": "Conformez-vous à la réglementation en vigueur grâce à l'enregistrement numérique des invités. Faites le check-in en ligne ou laissez vos clients le faire eux-mêmes.",
  "land.home.f5.t": "Facturation intégrée et sans complications",
  "land.home.f5.d": "Émettez des factures numériques pour chaque réservation et envoyez-les directement à vos invités. Enregistrez toutes les charges facilement.",

  "land.home.visibilidadTitle": "Augmentez votre visibilité et évitez les erreurs de réservation",
  "land.home.visibilidad1": "Avec EasyRural, oubliez les overbookings et la mise à jour manuelle de chaque calendrier. Notre solution se connecte avec 100+ portails via intégration iCal.",
  "land.home.visibilidad2": "Chaque fois qu'une nouvelle réservation est enregistrée, le système met à jour tous les canaux où vous êtes présent, maintenant l'information cohérente.",
  "land.home.portalesConect": "portails connectés",
  "land.home.entreOtros": "... et bien d'autres portails !",

  "land.banner.title": "Essayez EasyRural complètement gratuit et sans limites pendant 14 jours.",
  "land.banner.desc": "En seulement 10 minutes, EasyRural sera prêt et fonctionnel dans votre hébergement touristique, sans besoin d'installations compliquées.",

  "land.home.confianTitle": "Hébergements ruraux qui font confiance à notre PMS :",
  "land.home.opinanTitle": "Que disent les clients de nos systèmes ?",
  "land.home.test1.t": "Facile à utiliser et très efficace",
  "land.home.test1.q": "EasyRural a simplifié la gestion de notre refuge de montagne. Son interface intuitive facilite la gestion de réservations, facturation et enregistrement des invités.",
  "land.home.test2.t": "Économie de temps et d'effort",
  "land.home.test2.q": "Grâce à EasyRural, je peux maintenant consacrer plus de temps à mes invités et améliorer leur expérience au lieu de le perdre en tâches administratives.",
  "land.home.test3.t": "Simplicité pour débutants",
  "land.home.test3.q": "En tant que propriétaire d'un hébergement rural sans expérience préalable avec les outils numériques, EasyRural a été une grande découverte.",
  "land.home.test4.t": "Support technique exceptionnel !",
  "land.home.test4.q": "Ce qui m'a le plus impressionné chez EasyRural, c'est l'excellent service client. Chaque fois que j'ai eu une question, l'équipe a répondu rapidement.",

  "land.login.title": "Se connecter",
  "land.login.email": "Adresse e-mail",
  "land.login.emailPh": "Adresse e-mail...",
  "land.login.password": "Mot de passe",
  "land.login.passwordPh": "Mot de passe...",
  "land.login.err": "E-mail ou mot de passe incorrect",
  "land.login.btn": "Continuer avec l'e-mail",
  "land.login.terms1": "En vous connectant à EasyRural, vous acceptez nos",
  "land.login.terms2": "et notre",
  "land.login.privacy": "politique de confidentialité",
  "land.login.noAccount": "Vous n'avez pas de compte ?",
  "land.login.createAccount": "Créer un compte",

  "land.reg.title": "Créer un compte utilisateur",
  "land.reg.name": "Nom",
  "land.reg.namePh": "Nom...",
  "land.reg.passwordHelp": "Créez un mot de passe sécurisé avec au moins 8 caractères, incluant au moins une majuscule, une minuscule, un chiffre et un symbole.",
  "land.reg.confirm": "Confirmer le mot de passe",
  "land.reg.confirmPh": "Confirmer le mot de passe...",
  "land.reg.passMismatch": "Les mots de passe ne correspondent pas",
  "land.reg.passShort": "Le mot de passe doit faire au moins 8 caractères",
  "land.reg.sent": "Demande préparée. Nous vous contacterons bientôt.",
  "land.reg.btn": "Continuer avec l'e-mail",
  "land.reg.terms1": "En vous inscrivant à EasyRural, vous acceptez nos",
  "land.reg.haveAccount": "Vous avez déjà un compte ?",
  "land.reg.login": "Se connecter",

  "land.pre.title": "Commencez votre période d'essai gratuite aujourd'hui et utilisez le coupon",
  "land.pre.welcome": "WELCOME50",
  "land.pre.titleEnd": "pour obtenir une réduction de 50% pendant 3 mois.",
  "land.pre.intro": "Essayez EasyRural gratuitement pendant 14 jours et découvrez le confort de gérer efficacement votre hébergement rural.",
  "land.pre.tipo": "Indiquez si votre hébergement touristique est en location complète ou par chambres.",
  "land.pre.completo": "Complet",
  "land.pre.habitaciones": "Par chambres",
  "land.pre.planBasico": "Rural Basique",
  "land.pre.planBasicoSub": "Facilite et automatise les processus essentiels pour la gestion de votre hébergement de tourisme rural.",
  "land.pre.planRoomsSub": "Gérez les hébergements par chambres avec des tarifs évolutifs selon votre volume.",
  "land.pre.desde": "À partir de",
  "land.pre.mes": "€/mois",
  "land.pre.anio": "€/an",
  "land.pre.durante3": "Pendant 3 mois",
  "land.pre.descuento15": "Réduction 15%",
  "land.pre.kitDigital": "* Subventionnable avec le Kit Digital",
  "land.pre.feat1": "Système de réservations (PMS)",
  "land.pre.feat2": "Gestion de la relation invités (CRM)",
  "land.pre.feat3": "Génération de rapports policiers",
  "land.pre.feat4": "Contrôle de caisse et facturation",
  "land.pre.feat5": "Génération de rapports financiers",
  "land.pre.feat6": "1 utilisateur",
  "land.pre.feat7": "4 langues disponibles",
  "land.pre.feat8": "Support 365 jours par an",
  "land.pre.feat9": "10 chambres incluses",
  "land.pre.feat10": "2€ extra toutes les 10 chambres (Max. 60)",
  "land.pre.empresasTitle": "Vous êtes une entreprise gérant plus de 5 hébergements touristiques ?",
  "land.pre.empresasDesc": "Chez EasyRural nous offrons des tarifs spéciaux et personnalisés qui s'adaptent à votre volume d'activité.",
  "land.pre.contactanos": "Nous contacter",

  "land.eq.label": "Équipe",
  "land.eq.h1": "Numérisation, efficacité et simplicité",
  "land.eq.intro1": "EasyRural arrive pour briser les barrières et apporter la révolution numérique aux hébergements de tourisme rural.",
  "land.eq.intro2": "Assez de technologies compliquées et inaccessibles pour les professionnels du tourisme rural ! Nous naissons pour libérer le potentiel numérique des hébergements ruraux.",
  "land.eq.misionTit": "Notre mission",
  "land.eq.misionDesc": "Notre mission est de moderniser le monde rural à travers notre écosystème numérique.",
  "land.eq.visionTit": "Notre vision",
  "land.eq.visionDesc": "Nous ne nous arrêterons pas avant d'être l'entreprise leader dans la transformation numérique du tourisme rural.",
  "land.eq.valoresTit": "Nos valeurs",
  "land.eq.v1.t": "Innovation",
  "land.eq.v1.d": "Notre culture d'entreprise nous place à l'avant-garde, créant des solutions différentes et uniques.",
  "land.eq.v2.t": "Efficacité",
  "land.eq.v2.d": "Tous nos processus sont axés sur l'optimisation du temps et des résultats.",
  "land.eq.v3.t": "Passion pour le client",
  "land.eq.v3.d": "Nous mettons les personnes au centre de notre activité.",
  "land.eq.v4.t": "Spécialisation",
  "land.eq.v4.d": "Engagés à offrir une solution numérique adaptée aux professionnels du tourisme rural.",
  "land.eq.v5.t": "Durabilité",
  "land.eq.v5.d": "Toutes nos actions sont respectueuses de l'environnement et de la société.",
  "land.eq.detrasTit": "Qui est derrière EasyRural ?",
  "land.eq.m1.role": "PDG",
  "land.eq.m1.bio": "Professionnel du tourisme avec plus de 7 ans d'expérience dans la gestion d'hébergements touristiques. Spécialisé en projets de tourisme rural.",
  "land.eq.m2.role": "CTO",
  "land.eq.m2.bio": "Développeur d'applications et directeur de projets dans une grande variété d'applications d'entreprise. Cofondateur d'Avantiam.",

  "land.faq.title": "QUESTIONS FRÉQUENTES",
  "land.faq.q1": "EasyRural propose-t-il un essai gratuit ?",
  "land.faq.a1": "Oui, nous offrons un essai gratuit de 14 jours sans engagement.",
  "land.faq.q2": "Peut-on utiliser EasyRural depuis n'importe quel appareil ?",
  "land.faq.a2": "Oui, plateforme web responsive pour ordinateur, tablette et mobile.",
  "land.faq.q3": "Ai-je besoin d'expérience préalable avec les outils numériques ?",
  "land.faq.a3": "Non, interface intuitive avec instructions claires, accessible même pour les débutants.",
  "land.faq.q4": "Puis-je enregistrer plus d'un hébergement ?",
  "land.faq.a4": "Oui, connectez plusieurs hébergements à un seul utilisateur et changez depuis le menu.",
  "land.faq.q5": "Comment contacter EasyRural ?",
  "land.faq.a5": "Par email à info@easyrural.net (24/7) ou au +34 649 13 35 41 (lun-ven 8h-14h). Aussi sur Discord.",
  "land.faq.q6": "Puis-je connecter avec les portails de location ?",
  "land.faq.a6": "Nous travaillons sur la connectivité avec Booking, Airbnb, VRBO et Expedia via API. iCal pour 100+ portails.",
  "land.faq.q7": "Est-il possible de personnaliser les tarifs ?",
  "land.faq.a7": "Oui, ajustez les tarifs selon demande et saison avec prix haute/basse saison.",
  "land.faq.q8": "Puis-je personnaliser les politiques de réservation ?",
  "land.faq.a8": "Oui, règles d'annulation, politiques de dépôt et exigences spécifiques.",
  "land.faq.q9": "Le système génère-t-il des rapports policiers automatisés ?",
  "land.faq.a9": "Oui, crée des rapports automatiquement à partir des informations enregistrées.",
  "land.faq.q10": "Comment se fait la facturation des réservations ?",
  "land.faq.a10": "Factures numériques rapides envoyées directement aux invités par email.",
  "land.faq.q11": "Puis-je gérer les factures de fournisseurs ?",
  "land.faq.a11": "Oui, enregistrez et gérez les factures de fournisseurs avec rapprochement des paiements.",
  "land.faq.q12": "Comment cela m'aide-t-il avec la trésorerie ?",
  "land.faq.a12": "Suivi mis à jour en temps réel par mode de paiement.",
  "land.faq.q13": "Puis-je générer des rapports financiers ?",
  "land.faq.a13": "Oui, rapports détaillés de revenus et dépenses et résumés fiscaux pour votre comptable.",
  "land.faq.q14": "Comment améliorer l'expérience de mes invités ?",
  "land.faq.a14": "Le CRM centralise les informations. Personnalisez les interactions et augmentez la fidélité.",
  "land.faq.q15": "Quels sont les avantages d'enregistrer des fournisseurs ?",
  "land.faq.a15": "Simplifie l'introduction de factures et automatise la récupération de données.",
  "land.faq.q16": "Est-il sûr de stocker les informations des invités ?",
  "land.faq.a16": "Oui, EasyRural respecte le RGPD et applique des mesures de sécurité.",

  "land.ct.formTitle": "Formulaire de contact",
  "land.ct.alsoTitle": "Contactez-nous aussi",
  "land.ct.fName": "Nom complet *",
  "land.ct.fEmail": "Email *",
  "land.ct.fPhone": "Téléphone",
  "land.ct.fSubject": "Sujet *",
  "land.ct.fMessage": "Message *",
  "land.ct.fAccept": "J'ai lu et j'accepte la",
  "land.ct.fPrivacy": "POLITIQUE DE CONFIDENTIALITÉ",
  "land.ct.fSubmit": "Envoyer le message",
  "land.ct.sent": "Message préparé dans votre client de messagerie.",
  "land.ct.byEmail": "Email",
  "land.ct.byPhone": "Téléphone",
  "land.ct.byDiscord": "Discord",
  "land.ct.discordCta": "Rejoindre le serveur",
  "land.ct.discordSub": "Support par appel vidéo",

  "land.bl.title": "Blog EasyRural",
  "land.bl.subtitle": "Conseils, nouvelles et histoires de réussite pour gestionnaires d'hébergements ruraux.",
  "land.bl.read": "Lire l'article →",
  "land.bl.p1.t": "5 raisons de numériser votre gîte rural en 2026",
  "land.bl.p1.d": "Découvrez pourquoi la numérisation est essentielle pour rester compétitif.",
  "land.bl.p1.date": "12 jan 2026",
  "land.bl.p2.t": "Registre unique de location touristique : ce qu'il faut savoir",
  "land.bl.p2.d": "Guide sur le nouveau registre obligatoire pour locations touristiques en Espagne.",
  "land.bl.p2.date": "5 jan 2026",
  "land.bl.p3.t": "Synchronisation iCal : la clé pour éviter les overbookings",
  "land.bl.p3.d": "Comment maintenir votre calendrier à jour sur Booking, Airbnb et 100+ plateformes.",
  "land.bl.p3.date": "20 déc 2025",

  "land.fn.queIncluye": "Que comprend-il ?",
  "land.fn.empiezaGratis": "Commencer gratuitement",

  "land.fn.reservas.hero": "Centralisez et automatisez les réservations de votre hébergement rural dans une seule plateforme.",
  "land.fn.reservas.desc": "Vérifiez la disponibilité en temps réel, créez des devis automatisés et suivez les paiements depuis une seule plateforme.",
  "land.fn.reservas.f1.t": "Création et suivi complet des réservations",
  "land.fn.reservas.f1.d": "Vérifiez la disponibilité en temps réel, créez des devis et suivez les paiements.",
  "land.fn.reservas.f2.t": "Tarifs et calendrier sous contrôle",
  "land.fn.reservas.f2.d": "Ajustez tarifs et calendrier de façon flexible selon la saison.",
  "land.fn.reservas.f3.t": "Synchronisation automatique avec 100+ portails",
  "land.fn.reservas.f3.d": "Connectez avec OTAs comme Booking ou Airbnb. Évitez l'overbooking.",
  "land.fn.reservas.f4.t": "Enregistrement numérique des invités",
  "land.fn.reservas.f4.d": "Conformez-vous à la réglementation avec check-in en ligne.",
  "land.fn.reservas.f5.t": "Facturation intégrée",
  "land.fn.reservas.f5.d": "Factures numériques par réservation envoyées aux invités.",

  "land.fn.facturacion.hero": "Facturation électronique et contrôle financier pour votre gîte rural sans complications.",
  "land.fn.facturacion.desc": "Émettez des factures numériques, enregistrez les reçues et obtenez des rapports financiers automatisés.",
  "land.fn.facturacion.f1.t": "Facturation numérique par réservation",
  "land.fn.facturacion.f1.d": "Émettez des factures numériques en quelques secondes et envoyez-les automatiquement.",
  "land.fn.facturacion.f2.t": "Registre des factures reçues",
  "land.fn.facturacion.f2.d": "Contrôlez les factures des fournisseurs avec rapprochement des paiements.",
  "land.fn.facturacion.f3.t": "Contrôle de caisse en temps réel",
  "land.fn.facturacion.f3.d": "Visualisez l'état de caisse par mode de paiement.",
  "land.fn.facturacion.f4.t": "Rapports financiers automatiques",
  "land.fn.facturacion.f4.d": "Rapports détaillés de revenus et dépenses pour votre comptable.",
  "land.fn.facturacion.f5.t": "Conformité Verifactu / Factura-e",
  "land.fn.facturacion.f5.d": "Conformez-vous à la nouvelle réglementation espagnole sans effort.",

  "land.fn.huespedes.hero": "Votre CRM hôtelier adapté au tourisme rural.",
  "land.fn.huespedes.desc": "Centralisez les données des invités, automatisez les rapports policiers et fidélisez vos clients.",
  "land.fn.huespedes.f1.t": "CRM centralisé",
  "land.fn.huespedes.f1.d": "Toutes les informations des invités en un seul endroit. Historique et préférences.",
  "land.fn.huespedes.f2.t": "Check-in en ligne",
  "land.fn.huespedes.f2.d": "Permettez à vos invités de faire le check-in depuis leur mobile.",
  "land.fn.huespedes.f3.t": "Rapports policiers automatisés",
  "land.fn.huespedes.f3.d": "Générez et envoyez les déclarations aux autorités sans paperasse.",
  "land.fn.huespedes.f4.t": "Fidélisation et communication",
  "land.fn.huespedes.f4.d": "Personnalisez les interactions et augmentez la satisfaction.",
  "land.fn.huespedes.f5.t": "Conformité RGPD",
  "land.fn.huespedes.f5.d": "Stockez les données selon la réglementation européenne.",

  "land.fn.motor.hero": "Recevez des réservations directes depuis votre site sans commissions.",
  "land.fn.motor.desc": "Un moteur de réservation intégré dans votre site qui évite les commissions.",
  "land.fn.motor.f1.t": "Réservations directes sans commissions",
  "land.fn.motor.f1.d": "Les invités réservent directement depuis votre site. Zéro commission d'OTA.",
  "land.fn.motor.f2.t": "Design responsive",
  "land.fn.motor.f2.d": "Intégration visuelle adaptée à votre marque et tous les appareils.",
  "land.fn.motor.f3.t": "Disponibilité et tarifs synchronisés",
  "land.fn.motor.f3.d": "Lit la disponibilité de votre PMS — jamais de surventes.",
  "land.fn.motor.f4.t": "Paiement en ligne sécurisé",
  "land.fn.motor.f4.d": "Acceptez la carte et enregistrez les acomptes selon le flux du PMS.",
  "land.fn.motor.f5.t": "Multi-langue et multi-devise",
  "land.fn.motor.f5.d": "Atteignez des clients internationaux en ES, CA, EN et FR.",

  // ─── PANEL ADMIN / MULTI-UTILISATEUR (v9.1) ────────────
  "nav.adminOverview": "Panel admin",
  "nav.users": "Utilisateurs",

  "admin.title": "Panel d'administration",
  "admin.subtitle": "Vue rapide de tous les hébergements actifs sur EasyRural.",
  "admin.newClient": "+ Nouvel hébergement",
  "admin.kpi.active": "Actifs",
  "admin.kpi.activeSub": "dans {n} commune{s}",
  "admin.kpi.drafts": "Brouillons",
  "admin.kpi.draftsSub": "Sans forfait",
  "admin.kpi.complete": "Forfait complet",
  "admin.kpi.completeSub": "APIs + Capteurs IoT",
  "admin.kpi.basic": "Forfait basique",
  "admin.kpi.basicSub": "APIs externes uniquement",
  "admin.mapTitle": "CARTE GLOBALE",
  "admin.listTitle": "HÉBERGEMENTS",
  "admin.noHousings": "Aucun hébergement enregistré pour le moment.",

  "card.today": "Aujourd'hui",
  "card.month": "Mois",
  "card.costMonth": "Coût mois",
  "card.powerNow": "Puissance maint.",
  "card.openDash": "Ouvrir dashboard →",
  "card.completeData": "Compléter les données →",
  "card.draftNote": "Enregistré sur la plateforme mais sans forfait. Il manque le CUPS et/ou l'installation des capteurs.",
  "card.status.ok": "OK",
  "card.status.draft": "Brouillon",
  "card.status.alarm": "{n} alarme{s}",
  "card.plan.none": "sans forfait",

  "map.globalTitle": "Carte globale des hébergements",
  "map.title": "Carte des hébergements",
  "map.globalSubtitle": "Tous les hébergements enregistrés sur EasyRural — actifs et brouillons ({n})",
  "map.mineSubtitle": "Vos hébergements sur la carte ({n})",
  "map.badge.draft": "⚠ Brouillon · sans forfait",
  "map.badge.plan": "Forfait {plan}",
  "map.noCoords": "Aucun hébergement avec coordonnées configurées.",

  "empty.adminTitle": "Aucun hébergement pour l'instant",
  "empty.clientTitle": "Bonjour, {name}",
  "empty.clientTitleFallback": "Bonjour et bienvenue",
  "empty.adminBody": "Aucun hébergement n'a encore été enregistré sur la plateforme. Commencez par enregistrer le premier client — ses appareils apparaîtront ici automatiquement.",
  "empty.clientBody": "Votre hébergement n'est pas encore actif. Dès que l'équipe EasyRural aura terminé la configuration des capteurs, vous verrez ici votre consommation, empreinte carbone et alarmes en temps réel.",
  "empty.firstClient": "+ Enregistrer le premier client",
  "empty.goMap": "Voir la carte des services",
  "empty.contactHint": "Vous pensez qu'il y a une erreur ? Contactez",

  "users.title": "Utilisateurs et accès",
  "users.subtitle": "Comptes actifs de la démo et hébergement accessible par chaque compte.",
  "users.kpi.total": "Utilisateurs",
  "users.kpi.totalSub": "sur la plateforme",
  "users.kpi.admin": "Administrateurs",
  "users.kpi.adminSub": "Voient tous les hébergements",
  "users.kpi.multi": "Multi-hébergement",
  "users.kpi.multiSub": "Propriétaires de ≥2 maisons",
  "users.kpi.single": "Individuels",
  "users.kpi.singleSub": "Une seule maison",
  "users.col.name": "Nom",
  "users.col.email": "Email",
  "users.col.role": "Rôle",
  "users.col.access": "Hébergements accessibles",
  "users.col.password": "Mot de passe démo",
  "users.role.admin": "Administrateur",
  "users.role.cliente": "Client",
  "users.role.adminFull": "Admin global",
  "users.role.multi": "Multi-owner",
  "users.access.all": "Tous (brouillons inclus)",
  "users.demoNoteTitle": "⚠ Démo uniquement",
  "users.demoNote": "ces comptes sont codés en dur dans le frontend pour la présentation. En production, ils seront remplacés par une authentification réelle (JWT + hachage du mot de passe côté backend).",

  "edit.title": "Modifier {name}",
  "edit.subtitle": "Complétez ou mettez à jour les données de l'hébergement.",
  "edit.saveChanges": "Enregistrer les modifications",
  "edit.saveDraft": "Enregistrer comme brouillon",

  "newClient.draftToggle": "Enregistrer comme brouillon",
  "newClient.draftHelp": "L'hébergement est enregistré sur EasyRural (visible sur la carte) mais SANS forfait ni CUPS. Peut être complété plus tard.",
  "newClient.saveDraft": "Enregistrer brouillon",
};

const STRINGS = { es: ES, ca: CA, en: EN, fr: FR };

function interpolate(str, params) {
  if (!params || typeof str !== "string") return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (params[k] !== undefined ? params[k] : `{${k}}`));
}

export function translate(key, lang, params) {
  const dict = STRINGS[lang] || STRINGS.es;
  const fallback = STRINGS.es[key];
  const raw = dict[key] !== undefined ? dict[key] : (fallback !== undefined ? fallback : key);
  return interpolate(raw, params);
}

const LangContext = createContext({ lang: "es", setLang: () => {} });

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem("easyrural_lang") || "es");
  useEffect(() => { localStorage.setItem("easyrural_lang", lang); }, [lang]);
  const setLang = (l) => setLangState(l);
  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
}

export function useLang() { return useContext(LangContext); }

export function useT() {
  const { lang } = useContext(LangContext);
  return (key, params) => translate(key, lang, params);
}

export const SUPPORTED_LANGS = [
  { code: "es", label: "ES" },
  { code: "ca", label: "CA" },
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
];
